import supportContact from '@/config/support-contact.json';
import { helpPath, requestedHelpAudience } from './helpNavigation.mjs';
import useSupportLanguage from './useSupportLanguage.jsx';
import { clinicalOutputsHelp } from "./clinicalOutputsHelp.js";
import { clinicianPayoutHelp } from "./clinicianPayoutHelp.js";
import React from "react";
import { Link, Navigate, useNavigate, useParams, useLocation } from "react-router-dom";
import Button from "@/components/ui/Button.jsx";
import AfriSwitch from "@/components/ui/AfriSwitch.jsx";
import LayoutShell from "@/components/ui/LayoutShell.jsx";
import SettingsCard from "@/sot/primitives/SettingsCard.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import PharmacyDesktopHeader from "@/components/nav/PharmacyDesktopHeader.jsx";
import PharmacyFolaSmartCard from "@/pages/pharmacy/PharmacyFolaSmartCard.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import useHelpLanguage from "./useHelpLanguage.jsx";
import {
  PHARMACY_NOTIFICATIONS_CHANGED_EVENT,
  readPharmacyNotifications,
} from "@/services/pharmacyLaneService.js";
import {
  getHelpArticle,
  getHelpArticlesForAudience,
  getHelpCategoriesForAudience,
  getHelpCategory,
  resolveHelpCategory,
  resolveHelpArticle,
} from "./helpContent.js";
import {
  HelpArticleCard,
  HelpCallout,
  HelpCategoryCard,
  HelpEmptyState,
  HelpMobileSectionNav,
  HelpScreenshotFrame,
  HelpSearchBar,
  HelpSection,
  HelpSidebar,
  HelpStepList,
  HelpSupportActionBlock,
} from "./components.jsx";

const HELP_AUDIENCE_OPTIONS = [
  { key: "patient", label: "Patient" },
  { key: "doctor", label: "Doctor" },
  { key: "pharmacy", label: "Pharmacy" },
];

const HELP_FRENCH_AUDIENCE_OPTIONS = [
  { key: "patient", label: "Patient" },
  { key: "doctor", label: "Médecin" },
  { key: "pharmacy", label: "Pharmacie" },
];

const HELP_FRENCH_CATEGORY_COPY = {
  "clinical-outputs": {title:"Documents cliniques et échange sécurisé",summary:clinicalOutputsHelp.fr.summary},
  "clinician-settlement": { title: "Versements des consultations", summary: clinicianPayoutHelp.fr.summary },
  "patient:getting-started": {
    title: "Premiers pas",
    summary:
      "Créez votre compte patient, connectez-vous en sécurité et comprenez les premiers choix demandés par AfriMed™.",
  },
  "patient:profile": {
    title: "Votre profil",
    summary:
      "Gérez les informations qu’AfriMed™ utilise pour les soins, la communication, les personnes à charge et les dossiers sensibles.",
  },
  "patient:messages-fola": {
    title: "Messages et Fola",
    summary:
      "Utilisez les messages pour les échanges de soins et comprenez comment Fola aide sans remplacer les cliniciens.",
  },
  "patient:appointments": {
    title: "Rendez-vous",
    summary: "Réserver, consulter, modifier et préparer vos rendez-vous.",
  },
  "patient:prescriptions-myehr": {
    title: "Prescriptions et mon DME",
    summary:
      "Dossiers de prescription, vérification, historique des visites, notes et contexte de santé lisible.",
  },
  "patient:payments-insurance": {
    title: "Paiements et assurance",
    summary:
      "Modes de paiement sécurisés, confirmation, récupération, annulations de paiement, remboursements, reçus et visibilité de l’assurance.",
  },
  "patient:dependants-family-care": {
    title: "Personnes à charge et soins familiaux",
    summary:
      "Soins gérés par un responsable, rendez-vous pour les personnes à charge et futurs relais de confidentialité.",
  },
  "patient:video-consultations": {
    title: "Consultations vidéo",
    summary:
      "Rejoindre une consultation vidéo, vérifier l’accès et préparer les soins à distance.",
  },
  "patient:privacy-security": {
    title: "Confidentialité et sécurité",
    summary:
      "Sécurité du compte, protection du mot de passe, confidentialité des données de santé et accès authentifié.",
  },
  "patient:technical-support": {
    title: "Assistance technique",
    summary:
      "Résoudre les problèmes d’accès, d’appareils, de notifications et de demandes d’assistance.",
  },
  "doctor-getting-started": {
    title: "Bien démarrer comme médecin",
    summary:
      "Configurez un compte clinicien, terminez les étapes de vérification adaptées à staging et accédez aux réglages médecin.",
  },
  "doctor-profile-setup": {
    title: "Configuration du profil médecin",
    summary:
      "Tenez à jour les informations que les patients et équipes de soins utilisent pour reconnaître votre pratique.",
  },
  "doctor-verification-credentials": {
    title: "Vérification et justificatifs",
    summary:
      "Suivez l’identité, l’inscription, la formation et les justificatifs sans surestimer l’état de vérification.",
  },
  "doctor-availability-consultancies": {
    title: "Disponibilités et lieux de consultation",
    summary:
      "Préparez les lieux de pratique, disponibilités, services et consultations pour la réservation.",
  },
  "doctor-appointments": {
    title: "Rendez-vous",
    summary:
      "Consultez les demandes, préparez les visites et gérez honnêtement l’état des consultations.",
  },
  "doctor-messaging-ehr": {
    title: "Messages, orientations et accès EHR",
    summary:
      "Communiquez avec les patients et demandez l’accès aux dossiers uniquement dans le bon contexte de soins.",
  },
  "doctor-prescriptions-notes": {
    title: "Prescriptions, Fola et notes SOAP",
    summary:
      "Utilisez les prescriptions et outils d’aide clinique avec revue professionnelle et responsabilité.",
  },
  "doctor-privacy-conduct": {
    title: "Confidentialité et conduite professionnelle",
    summary:
      "Gardez les données patient, l’identité clinicienne et les actions de pratique dans les limites professionnelles appropriées.",
  },
  "pharmacy:getting-started": {
    title: "Premiers pas",
    summary:
      "Entrez dans l’espace pharmacie, comprenez les données de démonstration et sachez quoi tester en premier.",
  },
  "pharmacy:pharmacy-profile-verification": {
    title: "Profil pharmacie et vérification",
    summary:
      "Configurez le profil de l’officine en staging tout en gardant la vérification et les paiements clairement en attente.",
  },
  "pharmacy:inventory-management": {
    title: "Gestion du stock",
    summary:
      "Consultez le stock de démonstration, ajoutez des articles et comprenez comment la disponibilité soutient la confiance dans l’exécution.",
  },
  "pharmacy:prescription-fulfilment": {
    title: "Exécution des prescriptions",
    summary:
      "Utilisez la file d’exécution de démonstration pour tester réception, préparation, vérification, délivrance et clôture.",
  },
  "pharmacy:payments-settlement": {
    title: "Paiements et règlement",
    summary:
      "Comprenez les lignes de paiement staging, les revenus et la préparation future des versements.",
  },
  "pharmacy:messages-fola": {
    title: "Messages et Fola™",
    summary:
      "Utilisez les prompts Fola™ sûrs pour la pharmacie sans exposer de pouvoirs cliniques ou administratifs inachevés.",
  },
  "pharmacy:team-members-permissions": {
    title: "Équipe et permissions",
    summary:
      "Invitez les membres de l’officine, attribuez les rôles et comprenez ce que chaque rôle peut faire.",
  },
  "pharmacy:notifications-alerts": {
    title: "Notifications et alertes",
    summary:
      "Utilisez les compteurs, alertes de prescription, alertes de stock et cartes du tableau de bord pour repérer le travail de l’officine.",
  },
  "pharmacy:technical-support": {
    title: "Assistance technique",
    summary:
      "Sachez quoi signaler lorsque les données staging, le routage, l’hydratation ou l’accès pharmacie se comportent mal.",
  },
};

const HELP_FRENCH_ARTICLE_COPY = {
  "interoperability": clinicalOutputsHelp.fr,
  "clinician-payout-account": clinicianPayoutHelp.fr,
  "patient-onboarding-overview": {
    title: "Aperçu de l’inscription patient",
    summary:
      "Passez de l’accès staging à la configuration patient sans mélanger le contexte d’un compte clinicien.",
  },
  "patient-sign-in-magic-link": {
    title: "Connexion et lien sécurisé",
    summary:
      "Entrez dans staging via l’écran d’invitation, inscrivez-vous par email et demandez un lien sécurisé si nécessaire.",
  },
  "patient-role-name": {
    title: "Choisir le rôle patient et saisir votre nom",
    summary:
      "Commencez dans le parcours patient et saisissez les noms correspondant à votre pièce d’identité officielle.",
  },
  "patient-phone-otp": {
    title: "Vérifier le téléphone ou continuer en toute sécurité en staging",
    summary:
      "Ajoutez un numéro de téléphone et comprenez ce que signifie une vérification OTP en attente pendant staging.",
  },
  "patient-passcode": {
    title: "Créer et confirmer le code d’accès",
    summary:
      "Définissez le code d’accès utilisé pour protéger l’accès rapide à votre compte AfriMed™.",
  },
  "patient-dob-intro-complete": {
    title: "Ajouter la date de naissance et terminer l’introduction",
    summary:
      "Terminez les informations d’introduction partagées avant le début de la configuration propre au patient.",
  },
  "patient-id-passport-consent": {
    title: "Consentement et vérification de la pièce d’identité / passeport",
    summary:
      "Ajoutez soigneusement les informations d’identité et comprenez le consentement avant le traitement des documents.",
  },
  "patient-id-ocr-language": {
    title: "OCR de la pièce d’identité / passeport et langue",
    summary:
      "Utilisez l’OCR uniquement lorsqu’il est proposé et choisissez la langue qui vous aide à avancer avec confiance dans AfriMed™.",
  },
  "patient-blood-type-conditions": {
    title: "Groupe sanguin et conditions médicales",
    summary:
      "Ajoutez les informations de santé de base qu’AfriMed™ utilise pour soutenir le contexte de soins.",
  },
  "patient-allergies-medications": {
    title: "Allergies et médicaments",
    summary:
      "Enregistrez allergies et médicaments afin que les équipes de soins disposent d’un contexte plus sûr.",
  },
  "patient-complete-open-settings": {
    title: "Terminer l’inscription et ouvrir les réglages",
    summary:
      "Terminez l’inscription patient, puis vérifiez le profil patient enregistré dans les réglages.",
  },
  "what-fola-is": {
    title: "Ce qu’est Fola",
    summary:
      "Fola est l’assistant d’accompagnement d’AfriMed™ pour une aide plus claire et plus sûre dans la plateforme.",
  },
  "what-fola-is-not": {
    title: "Ce que Fola n’est pas",
    summary: "Comprenez ses limites afin que la confiance en santé reste claire.",
  },
  "supported-staging-languages": {
    title: "Langues prises en charge en staging",
    summary:
      "Comprenez comment les demandes de langue fonctionnent pendant qu’AfriMed™ élargit encore sa couverture linguistique.",
  },
  "messaging-a-clinician": {
    title: "Envoyer un message à un clinicien",
    summary:
      "Utilisez les Messages AfriMed™ pour garder les échanges de rendez-vous et de soins au même endroit.",
  },
  "thread-summaries": {
    title: "Résumés de conversation",
    summary: "Utilisez les résumés pour comprendre plus facilement les longues conversations.",
  },
  "draft-replies": {
    title: "Brouillons de réponse",
    summary: "Utilisez l’aide à la rédaction pour écrire des messages plus clairs sans envoi automatique.",
  },
  "medication-explanations": {
    title: "Explications sur les médicaments",
    summary:
      "Demandez des explications en langage courant tout en gardant l’autorité clinique auprès de votre clinicien.",
  },
  "support-only-positioning": {
    title: "Positionnement d’assistance uniquement",
    summary: "Comprenez la promesse qu’AfriMed™ fait avec Fola pendant staging.",
  },
  "booking-an-appointment": {
    title: "Réserver un rendez-vous",
    summary: "Choisissez une option de soins, vérifiez les disponibilités et confirmez les détails du rendez-vous.",
  },
  "changing-or-cancelling": {
    title: "Modifier ou annuler un rendez-vous",
    summary: "Sachez où vérifier les détails du rendez-vous avant toute modification.",
  },
  "preparing-for-a-visit": {
    title: "Préparer une visite",
    summary: "Vérifiez les détails pratiques avant un rendez-vous en personne ou à distance.",
  },
  "edit-intro": {
    title: "Modifier l’introduction",
    summary:
      "Mettez à jour les principales informations de profil utilisées pour vous reconnaître dans AfriMed™.",
  },
  "contact-data": {
    title: "Coordonnées",
    summary:
      "Gardez vos informations email, téléphone et adresse à jour pour les communications de rendez-vous et de compte.",
  },
  dependants: {
    title: "Personnes à charge",
    summary:
      "Ajoutez et gérez les membres de la famille dont vous coordonnez les soins depuis votre compte.",
  },
  "health-insurance": {
    title: "Assurance santé",
    summary:
      "Enregistrez les informations d’assurance pour la visibilité pendant la réservation et la revue du profil.",
  },
  "medical-history": {
    title: "Antécédents médicaux",
    summary:
      "Utilisez AfriMed™ pour garder les informations de santé importantes lisibles et disponibles pour les soins.",
  },
  "privacy-expectations": {
    title: "Attentes de confidentialité",
    summary:
      "Comprenez comment AfriMed™ traite les informations sensibles de profil et de soins.",
  },
  "viewing-prescriptions": {
    title: "Consulter les prescriptions",
    summary:
      "Retrouvez les dossiers de prescription et comprenez les informations disponibles.",
  },
  "understanding-myehr": {
    questions: clinicalOutputsHelp.fr.questions.slice(0,2),
    title: "Comprendre mon DME",
    summary:
      "Utilisez mon DME comme contexte de santé lisible pour les visites, prescriptions et antécédents de soins.",
  },
  "downloading-records": {
    title: "Télécharger les dossiers",
    summary:
      "Préparez les informations de prescription ou de dossier de santé pour consultation personnelle lorsque les téléchargements sont activés.",
  },
  "choosing-payment-method": {
    title: "Choisir un mode de paiement",
    summary: "Choisissez un mode de paiement AfriMed™ disponible et comprenez quand la réservation est créée.",
  },
  "receipts-and-invoices": {
    title: "Reçus et factures",
    summary:
      "Ouvrez, imprimez et téléchargez les preuves de rendez-vous et de remboursement depuis Transactions et factures.",
  },
  "insurance-selection": {
    title: "Sélection de l’assurance",
    summary:
      "Enregistrez la visibilité de l’assurance sans supposer une automatisation réelle des demandes de remboursement.",
  },
  "sponsor-payment-links": {
    title: "Liens de paiement par sponsor",
    summary:
      "Utilisez un lien de paiement sponsor AfriMed™ encadré pour l’aide de la famille ou de la diaspora.",
  },
  "payment-status-recovery-refunds": {
    title: "Statut, récupération, annulation et remboursement du paiement",
    summary:
      "Comprenez les statuts de paiement en attente, échoué, refusé, expiré, annulé et remboursé.",
  },
  "adding-a-dependant": {
    title: "Ajouter une personne à charge",
    summary: "Créez un profil pour une personne dont vous coordonnez les soins.",
  },
  "booking-for-a-dependant": {
    title: "Réserver pour une personne à charge",
    summary: "Assurez-vous que le rendez-vous est rattaché au bon membre de la famille.",
  },
  "dependant-privacy": {
    title: "Confidentialité des personnes à charge",
    summary:
      "Comprenez ce qui reste en cours d’affinement pour la confidentialité des soins familiaux.",
  },
  "joining-a-video-consultation": {
    title: "Rejoindre une consultation vidéo",
    summary:
      "Utilisez les détails du rendez-vous pour rejoindre une visite à distance lorsque la vidéo est activée.",
  },
  "checking-video-access": {
    title: "Vérifier l’accès vidéo",
    summary:
      "Préparez votre navigateur, caméra, micro et connexion avant une visite à distance.",
  },
  "keeping-your-account-secure": {
    title: "Sécuriser votre compte",
    summary:
      "Protégez le compte AfriMed™ qui contient rendez-vous, messages et dossiers de santé.",
  },
  "health-information-privacy": {
    title: "Confidentialité des informations de santé",
    summary:
      "Comprenez les attentes de confidentialité autour des données de profil, de messages et de dossiers.",
  },
  "troubleshooting-access": {
    title: "Résoudre les problèmes d’accès",
    summary:
      "Passez en revue les raisons les plus fréquentes pour lesquelles AfriMed™ peut ne pas s’ouvrir ou se connecter correctement.",
  },
  "browser-device-notifications": {
    title: "Navigateur, appareil et notifications",
    summary:
      "Vérifiez les bases lorsqu’une page, un téléversement, une caméra ou une notification ne se comporte pas comme prévu.",
  },
  "doctor-onboarding-overview": {
    title: "Aperçu de l’inscription médecin",
    summary:
      "Passez de la sélection du rôle à la configuration clinicienne sans résidu de compte patient.",
  },
  "doctor-sign-in-magic-link": {
    title: "Connexion et lien sécurisé",
    summary:
      "Entrez dans staging via l’écran d’invitation, inscrivez-vous par email et demandez un lien sécurisé si nécessaire.",
  },
  "doctor-role-name": {
    title: "Choisir le rôle clinicien et saisir votre nom",
    summary:
      "Commencez dans le parcours clinicien et saisissez les noms correspondant à votre pièce d’identité officielle.",
  },
  "doctor-phone-otp": {
    title: "Vérifier le téléphone ou continuer en toute sécurité en staging",
    summary:
      "Ajoutez un numéro de téléphone et comprenez ce que signifie une vérification OTP en attente pendant staging.",
  },
  "doctor-passcode": {
    title: "Créer et confirmer le code d’accès",
    summary:
      "Définissez le code d’accès utilisé pour protéger l’accès rapide à votre compte AfriMed™.",
  },
  "doctor-dob-intro-complete": {
    title: "Ajouter la date de naissance et terminer l’introduction",
    summary:
      "Terminez les informations d’introduction partagées avant le début de la configuration propre au clinicien.",
  },
  "doctor-id-passport-staging-bypass": {
    title: "Vérification de la pièce d’identité / passeport et contournement staging",
    summary:
      "Ajoutez les informations d’identité sans présenter le contournement staging comme une vérification terminée.",
  },
  "doctor-languages-specialty": {
    title: "Langues, spécialité et sous-spécialités",
    summary:
      "Indiquez à AfriMed™ les langues que vous utilisez et le domaine clinique qui représente le mieux votre pratique.",
  },
  "doctor-subspecialty-registration-upload": {
    title: "Téléversement d’inscription et détails professionnels",
    summary:
      "Ajoutez le contexte de sous-spécialité et téléversez une preuve d’inscription professionnelle pour revue ultérieure.",
  },
  "doctor-formation-registration-details": {
    title: "Formation et détails d’inscription",
    summary: "Enregistrez avec exactitude les informations de formation et d’inscription professionnelle.",
  },
  "doctor-about-fola-bio": {
    title: "À propos de vous et brouillon de bio avec Fola",
    summary:
      "Rédigez une bio clinicien visible par les patients et utilisez l’aide Fola avec prudence.",
  },
  "doctor-complete-open-settings": {
    title: "Terminer l’inscription et ouvrir les réglages",
    summary:
      "Terminez l’inscription clinicien, puis vérifiez le profil médecin enregistré dans les réglages.",
  },
  "clinician-intro-profile": {
    title: "Introduction et profil clinicien",
    summary: "Vérifiez votre identité médecin visible après l’inscription.",
  },
  "specialty-subspecialty": {
    title: "Spécialité et sous-spécialité",
    summary:
      "Gardez les informations de spécialité clinique exactes pour la découverte et le contexte du rendez-vous.",
  },
  "languages-and-care-context": {
    title: "Langues et contexte de soins",
    summary: "Indiquez les langues que vous pouvez utiliser pendant les consultations.",
  },
  "identity-and-registration": {
    title: "Identité et inscription professionnelle",
    summary: "Enregistrez les informations d’identité clinicienne pour une revue de vérification ultérieure.",
  },
  "formation-and-credentials": {
    title: "Formation et justificatifs",
    summary: "Enregistrez les informations de formation et de justificatifs cliniques pour revue.",
  },
  "credential-upload-protection": {
    title: "Protection des téléversements de justificatifs",
    summary:
      "Comprenez comment les documents d’inscription téléversés sont traités pendant la vérification.",
  },
  "practice-facility-setup": {
    title: "Configuration du cabinet et de l’établissement",
    summary: "Associez le bon contexte de clinique ou de cabinet aux services du médecin.",
  },
  "services-and-consultancies": {
    title: "Services et consultations",
    summary: "Configurez les types de consultation que les patients peuvent demander ou réserver.",
  },
  "availability-basics": {
    title: "Bases des disponibilités",
    summary: "Gardez les disponibilités de rendez-vous alignées avec vos vraies heures de consultation.",
  },
  "reviewing-appointments": {
    title: "Consulter les rendez-vous",
    summary: "Utilisez le tableau de bord médecin pour comprendre les soins à venir.",
  },
  "changing-appointment-state": {
    title: "Modifier l’état d’un rendez-vous",
    summary: "Évitez de suggérer qu’un rendez-vous a changé avant l’enregistrement du nouvel état par AfriMed™.",
  },
  "messaging-patients": {
    title: "Messages aux patients",
    summary: "Utilisez les Messages AfriMed™ pour les échanges de rendez-vous et de soins.",
  },
  "requesting-ehr-access": {
    questions: clinicalOutputsHelp.fr.questions.slice(0,2),
    title: "Demander l’accès au DME",
    summary: "Demandez l’accès au dossier patient lorsque la relation de soins le justifie.",
  },
  referrals: {
    title: "Orientations",
    summary: "Créez et vérifiez les orientations sans exposer les données patient hors du parcours de soins.",
  },
  prescriptions: {
    title: "Prescriptions",
    summary: "Préparez les prescriptions dans le bon contexte de rendez-vous et de patient.",
  },
  "fola-for-clinicians": {
    title: "Fola pour les cliniciens",
    summary: "Utilisez Fola comme assistant de rédaction et de synthèse, pas comme décideur clinique.",
  },
  "vitalrep-soap-notes": {
    title: "VitalRep et notes SOAP",
    summary: "Comprenez où l’aide aux notes clinicien s’inscrit lorsqu’elle est disponible.",
  },
  "patient-data-boundaries": {
    title: "Limites des données patient",
    summary: "Évitez les expositions entre rôles ou entre patients lors de l’utilisation d’AfriMed™.",
  },
  "desktop-dashboard-use": {
    title: "Utiliser le tableau de bord desktop",
    summary:
      "Utilisez des écrans plus larges pour parcourir les rendez-vous, messages, réglages et contextes cliniques.",
  },
  "pharmacy-dashboard-quick-start": {
    title: "Démarrage rapide du tableau de bord pharmacie",
    summary:
      "Passez de l’accès pharmacie staging à un tableau de bord avec stock de démonstration et demandes d’exécution exemples.",
  },
  "complete-staging-onboarding": {
    title: "Terminer l’onboarding pharmacie en staging",
    summary:
      "Créez la structure de l’officine, confirmez les détails opérationnels et lancez sans blocage de vérification production.",
  },
  "seeded-stock-and-catalogue": {
    title: "Utiliser le stock de démonstration et ajouter l’inventaire",
    summary:
      "Comprenez pourquoi les officines staging commencent avec du stock exemple et comment ajouter ou modifier les lignes catalogue.",
  },
  "fulfilment-playbook": {
    title: "Vérifier, tarifer, préparer et remettre",
    summary:
      "Suivez le guide pharmacie pour les demandes de prescription de démonstration pendant staging.",
  },
  "staging-payments-and-revenue": {
    title: "Lire les paiements et revenus staging",
    summary:
      "Utilisez de fausses lignes payées pour valider les revenus du tableau de bord sans capture de paiement réelle.",
  },
  "pharmacy-fola-safe-prompts": {
    title: "Utiliser les conseils Fola™ sûrs pour la pharmacie",
    summary:
      "Interrogez Fola™ sur les rôles d’officine, la configuration du stock, les imports CSV, les étapes de remise et les concepts de paiement staging.",
  },
  "invite-staff-and-assign-roles": {
    title: "Inviter l’équipe et attribuer les rôles d’officine",
    summary:
      "Utilisez les réglages pharmacie pour ajouter des membres d’équipe tout en gardant les privilèges propriétaire contrôlés.",
  },
  "notification-counts-and-alerts": {
    title: "Vérifier les alertes de prescription et de stock",
    summary:
      "Gardez la cloche de notification, les compteurs de file, les métriques du tableau de bord et les alertes de stock alignés.",
  },
  "report-staging-pharmacy-issue": {
    title: "Signaler un problème pharmacie en staging",
    summary:
      "Rassemblez les bons détails lorsque l’onboarding, les données du tableau de bord, les messages ou l’exécution pharmacie ne se comportent pas comme prévu.",
  },
};

const HELP_FRENCH_TEXT_COPY = {
  "Choose Patient when AfriMed™ asks how you want to use the app.":
    "Choisissez Patient lorsque AfriMed™ vous demande comment vous souhaitez utiliser l’application.",
  "Complete the shared intro steps using your own name, phone number, passcode, date of birth, and account details.":
    "Terminez les étapes d’introduction partagées avec votre propre nom, numéro de téléphone, code d’accès, date de naissance et informations de compte.",
  "If OTP delivery is unavailable during staging, use Verify later only when shown. Deferred verification remains pending.":
    "Si l’envoi OTP n’est pas disponible pendant staging, utilisez Vérifier plus tard uniquement lorsque cette option est affichée. La vérification différée reste en attente.",
  "Continue through patient profile setup, including ID/passport, language, blood type, conditions, allergies, and medications.":
    "Continuez la configuration du profil patient, y compris pièce d’identité/passeport, langue, groupe sanguin, conditions, allergies et médicaments.",
  "Use Go to settings at completion so your patient profile can be reviewed before wider use.":
    "Utilisez Aller aux réglages à la fin afin de revoir votre profil patient avant une utilisation plus large.",
  "Patient context": "Contexte patient",
  "Patient onboarding should save patient profile details only. It should not hydrate from a clinician session or local draft after completion.":
    "L’inscription patient doit enregistrer uniquement les informations du profil patient. Elle ne doit pas se remplir depuis une session clinicienne ou un brouillon local après finalisation.",
  "Sign in and magic link": "Connexion et lien sécurisé",
  "Start from private staging access, choose email registration, and use the magic-link screen when signing in.":
    "Commencez depuis l’accès privé staging, choisissez l’inscription par email et utilisez l’écran de lien sécurisé pour vous connecter.",
  "Choose patient, then confirm your name": "Choisissez patient, puis confirmez votre nom",
  "Select the patient role first, then enter the legal names that match your official ID.":
    "Sélectionnez d’abord le rôle patient, puis saisissez les noms légaux correspondant à votre pièce d’identité officielle.",
  "Open the private staging access link from your invitation.":
    "Ouvrez le lien d’accès privé staging depuis votre invitation.",
  "Enter the invited email address and continue into AfriMed™ staging.":
    "Saisissez l’adresse email invitée et continuez vers AfriMed™ staging.",
  "Choose Register with email when creating your patient account.":
    "Choisissez S’inscrire avec l’email lors de la création de votre compte patient.",
  "Use the Email sign-in option to request a magic link if you are returning without a password flow.":
    "Utilisez l’option de connexion par email pour demander un lien sécurisé si vous revenez sans parcours de mot de passe.",
  "Only open sign-in links that you requested yourself.":
    "N’ouvrez que les liens de connexion que vous avez demandés vous-même.",
  "Invite-only access": "Accès sur invitation uniquement",
  "Staging access is private. Use the invited email address so the account stays attached to the right tester and patient context.":
    "L’accès staging est privé. Utilisez l’adresse email invitée afin que le compte reste associé au bon testeur et au bon contexte patient.",
  "Staging access and email sign-in": "Accès staging et connexion par email",
  "Begin from private staging access, register by email, or request a magic link when returning.":
    "Commencez depuis l’accès privé staging, inscrivez-vous par email ou demandez un lien sécurisé lors de votre retour.",
  "Avoid continuing as Clinician if you are setting up a patient account.":
    "Évitez de continuer comme Clinicien si vous configurez un compte patient.",
  "Enter your first name exactly as it appears on your official ID.":
    "Saisissez votre prénom exactement comme il apparaît sur votre pièce d’identité officielle.",
  "Enter your family name exactly as it appears on your official ID.":
    "Saisissez votre nom de famille exactement comme il apparaît sur votre pièce d’identité officielle.",
  "Continue only after the name fields are correct.":
    "Continuez uniquement lorsque les champs de nom sont corrects.",
  "Patient role context": "Contexte du rôle patient",
  "The patient role keeps care, booking, records, and dependant information separate from clinician tools.":
    "Le rôle patient sépare les soins, les réservations, les dossiers et les informations des personnes à charge des outils cliniciens.",
  "Role selection and legal name": "Sélection du rôle et nom légal",
  "Select Patient first, then enter names that match your official identification.":
    "Sélectionnez d’abord Patient, puis saisissez les noms correspondant à votre pièce d’identité officielle.",
  "Enter the phone number you want attached to your patient account.":
    "Saisissez le numéro de téléphone que vous souhaitez associer à votre compte patient.",
  "Use the OTP field when SMS delivery is available.":
    "Utilisez le champ OTP lorsque l’envoi SMS est disponible.",
  "If staging offers Verify later, use it only when delivery is unavailable or the screen clearly allows it.":
    "Si staging propose Vérifier plus tard, utilisez cette option uniquement lorsque l’envoi est indisponible ou que l’écran l’autorise clairement.",
  "Continue onboarding with the phone saved as pending, not verified.":
    "Continuez l’inscription avec le téléphone enregistré comme en attente, non vérifié.",
  "Return to phone verification when live OTP delivery is available.":
    "Revenez à la vérification du téléphone lorsque l’envoi OTP réel est disponible.",
  "Pending is not verified": "En attente ne signifie pas vérifié",
  "A deferred staging OTP helps you continue setup. It does not confirm phone ownership or enable SMS security by itself.":
    "Un OTP staging différé vous aide à continuer la configuration. Il ne confirme pas la propriété du téléphone et n’active pas à lui seul la sécurité SMS.",
  "Phone number and OTP": "Numéro de téléphone et OTP",
  "Add your phone number, verify by OTP when available, or continue only through the staging-safe path shown.":
    "Ajoutez votre numéro de téléphone, vérifiez par OTP lorsque disponible ou continuez uniquement par le parcours sûr staging affiché.",
  "Create a passcode you can remember without writing it in an unsafe place.":
    "Créez un code d’accès dont vous pouvez vous souvenir sans l’écrire dans un endroit non sécurisé.",
  "Confirm the same passcode on the next screen.":
    "Confirmez le même code d’accès sur l’écran suivant.",
  "If the confirmation does not match, re-enter both passcode steps carefully.":
    "Si la confirmation ne correspond pas, ressaisissez soigneusement les deux étapes du code d’accès.",
  "Avoid using a passcode that someone else can easily guess.":
    "Évitez d’utiliser un code d’accès qu’une autre personne pourrait deviner facilement.",
  "Account protection": "Protection du compte",
  "Your passcode protects access to AfriMed™ on shared or frequently handled devices.":
    "Votre code d’accès protège l’accès à AfriMed™ sur les appareils partagés ou souvent manipulés.",
  "Passcode setup": "Configuration du code d’accès",
  "Create and confirm the same passcode before moving into the next onboarding step.":
    "Créez et confirmez le même code d’accès avant de passer à l’étape suivante de l’inscription.",
  "Enter your date of birth using the format shown on the screen.":
    "Saisissez votre date de naissance au format affiché à l’écran.",
  "Check the day, month, and year before continuing.":
    "Vérifiez le jour, le mois et l’année avant de continuer.",
  "Complete the intro screen once AfriMed™ confirms the shared setup section is done.":
    "Terminez l’écran d’introduction une fois qu’AfriMed™ confirme que la section de configuration partagée est terminée.",
  "Continue into patient-specific onboarding rather than stopping at the intro completion screen.":
    "Continuez vers l’inscription propre au patient au lieu de vous arrêter à l’écran de fin d’introduction.",
  "Date of birth and intro completion": "Date de naissance et fin de l’introduction",
  "Add your date of birth, then continue once the shared intro section is complete.":
    "Ajoutez votre date de naissance, puis continuez une fois la section d’introduction partagée terminée.",
  "Choose the ID or passport option shown for your account.":
    "Choisissez l’option pièce d’identité ou passeport affichée pour votre compte.",
  "Enter the identity details requested by the screen.":
    "Saisissez les informations d’identité demandées par l’écran.",
  "Read the consent prompt before continuing.":
    "Lisez la demande de consentement avant de continuer.",
  "Only consent when you are comfortable with the identity-processing step shown.":
    "Ne donnez votre consentement que si vous êtes à l’aise avec l’étape de traitement d’identité affichée.",
  "Treat any skipped or deferred identity review as pending until real verification is completed.":
    "Considérez toute revue d’identité ignorée ou différée comme en attente jusqu’à la fin de la vérification réelle.",
  "Consent is not verification": "Le consentement n’est pas une vérification",
  "Giving consent or entering document details does not by itself mean ID or passport verification has been completed.":
    "Donner son consentement ou saisir des informations de document ne signifie pas à lui seul que la vérification de la pièce d’identité ou du passeport est terminée.",
  "ID, passport, and consent": "Pièce d’identité, passeport et consentement",
  "Complete identity fields truthfully and read consent language before continuing.":
    "Renseignez les champs d’identité avec exactitude et lisez le texte de consentement avant de continuer.",
  "Use the OCR or document capture step only when AfriMed™ shows it.":
    "Utilisez l’étape OCR ou capture de document uniquement lorsqu’AfriMed™ l’affiche.",
  "Check any extracted details carefully before continuing.":
    "Vérifiez soigneusement toute information extraite avant de continuer.",
  "Do not treat OCR capture as completed verification unless AfriMed™ explicitly confirms verification.":
    "Ne considérez pas la capture OCR comme une vérification terminée sauf si AfriMed™ confirme explicitement la vérification.",
  "Choose the interface language you prefer for care and account setup.":
    "Choisissez la langue d’interface que vous préférez pour les soins et la configuration du compte.",
  "Return to language settings later if your preference changes.":
    "Revenez plus tard aux réglages de langue si votre préférence change.",
  "OCR is processing support": "L’OCR est une aide au traitement",
  "OCR can help read document details, but OCR capture alone does not prove identity verification is complete.":
    "L’OCR peut aider à lire les informations d’un document, mais la capture OCR seule ne prouve pas que la vérification d’identité est terminée.",
  "OCR and language": "OCR et langue",
  "Use OCR only when prompted, then select the language that helps you use AfriMed™ confidently.":
    "Utilisez l’OCR uniquement lorsqu’il est proposé, puis sélectionnez la langue qui vous aide à utiliser AfriMed™ avec confiance.",
  "Choose your blood type only if you know it accurately.":
    "Choisissez votre groupe sanguin uniquement si vous le connaissez avec exactitude.",
  "Use an unknown or skip option if you are not sure.":
    "Utilisez l’option inconnu ou ignorer si vous n’êtes pas sûr.",
  "Add medical conditions that are relevant to care.":
    "Ajoutez les conditions médicales pertinentes pour les soins.",
  "Avoid guessing health information just to complete the screen.":
    "Évitez de deviner des informations de santé simplement pour terminer l’écran.",
  "Review and update these details later in patient Settings when needed.":
    "Revoyez et mettez à jour ces informations plus tard dans les réglages patient si nécessaire.",
  "Accuracy matters": "L’exactitude compte",
  "Health profile fields should reflect what you know to be accurate. You can update them later as your records become clearer.":
    "Les champs du profil de santé doivent refléter ce que vous savez être exact. Vous pourrez les mettre à jour plus tard lorsque vos dossiers seront plus clairs.",
  "Blood type and conditions": "Groupe sanguin et conditions",
  "Add known blood type and relevant conditions without guessing uncertain health information.":
    "Ajoutez le groupe sanguin connu et les conditions pertinentes sans deviner les informations de santé incertaines.",
  "Add known allergies that may affect care, medicines, or prescriptions.":
    "Ajoutez les allergies connues qui peuvent influencer les soins, les médicaments ou les prescriptions.",
  "Use clear names where possible and avoid guessing uncertain allergy details.":
    "Utilisez des noms clairs lorsque possible et évitez de deviner les détails d’allergie incertains.",
  "Add current medications you are taking.":
    "Ajoutez les médicaments que vous prenez actuellement.",
  "Review spelling, dose, and medication names if the screen asks for them.":
    "Vérifiez l’orthographe, la dose et les noms de médicaments si l’écran les demande.",
  "Update allergies and medications later if your care details change.":
    "Mettez à jour les allergies et médicaments plus tard si vos informations de soins changent.",
  "Keep care context current": "Gardez le contexte de soins à jour",
  "Allergies and medication details can affect safe care decisions, so keep them current and accurate.":
    "Les détails d’allergies et de médicaments peuvent influencer la sécurité des décisions de soins ; gardez-les donc à jour et exacts.",
  "Allergies and medications": "Allergies et médicaments",
  "Add known allergies and current medications so your patient profile has safer care context.":
    "Ajoutez les allergies connues et les médicaments actuels afin que votre profil patient dispose d’un contexte de soins plus sûr.",
  "Complete the final onboarding screen after all required patient steps are done.":
    "Terminez le dernier écran d’inscription une fois toutes les étapes patient requises terminées.",
  "Use Go to settings when AfriMed™ offers it.":
    "Utilisez Aller aux réglages lorsqu’AfriMed™ le propose.",
  "Check the Settings welcome screen for the correct patient context.":
    "Vérifiez que l’écran d’accueil des réglages affiche le bon contexte patient.",
  "Open patient Settings and review profile, account, health, and preference details.":
    "Ouvrez les réglages patient et vérifiez le profil, le compte, les informations de santé et les préférences.",
  "Refresh or sign in again later to confirm the profile still hydrates from saved patient data.":
    "Actualisez ou reconnectez-vous plus tard pour confirmer que le profil se remplit toujours depuis les données patient enregistrées.",
  "Review your saved profile": "Vérifiez votre profil enregistré",
  "Settings is where the patient profile can be reviewed before booking care, managing records, or using dependant workflows.":
    "Les réglages permettent de vérifier le profil patient avant de réserver des soins, gérer des dossiers ou utiliser les parcours de personnes à charge.",
  "Completion and Settings welcome": "Finalisation et accueil des réglages",
  "Finish onboarding, then move into the Settings welcome screen for the patient account.":
    "Terminez l’inscription, puis passez à l’écran d’accueil des réglages du compte patient.",
  "Use Fola for AfriMed™ workflow questions, message support, and plain-language explanations.":
    "Utilisez Fola pour les questions de parcours AfriMed™, l’aide aux messages et les explications en langage simple.",
  "Ask for help understanding next steps inside AfriMed™.":
    "Demandez de l’aide pour comprendre les prochaines étapes dans AfriMed™.",
  "Use supported language requests during staging when available.":
    "Utilisez les demandes de langue prises en charge pendant staging lorsqu’elles sont disponibles.",
  "Review Fola output before acting on anything care-related.":
    "Relisez la réponse de Fola avant toute action liée aux soins.",
  "Support-only positioning": "Positionnement d’assistance uniquement",
  "Fola can help explain AfriMed™ workflows and selected care information, but it does not diagnose, prescribe, or replace your clinician.":
    "Fola peut aider à expliquer les parcours AfriMed™ et certaines informations de soins, mais il ne diagnostique pas, ne prescrit pas et ne remplace pas votre clinicien.",
  "Screenshot slot": "Emplacement de capture",
  "Add a message-thread screenshot showing Fola support language once approved.":
    "Ajoutez une capture de fil de messages montrant le langage d’assistance de Fola une fois approuvée.",
  "Do not use Fola as an emergency service.":
    "N’utilisez pas Fola comme service d’urgence.",
  "Do not treat Fola as a doctor or diagnostic tool.":
    "Ne considérez pas Fola comme un médecin ou un outil de diagnostic.",
  "Do not use Fola output as a prescription or treatment plan.":
    "N’utilisez pas les réponses de Fola comme prescription ou plan de traitement.",
  "Contact a clinician or emergency service for urgent medical concerns.":
    "Contactez un clinicien ou un service d’urgence pour toute préoccupation médicale urgente.",
  "Healthcare safety": "Sécurité des soins",
  "Fola is not an AI doctor. Clinical decisions remain with qualified clinicians.":
    "Fola n’est pas un médecin IA. Les décisions cliniques restent entre les mains de cliniciens qualifiés.",
  "Ask Fola to respond in a supported AfriMed™ staging language when needed.":
    "Demandez à Fola de répondre dans une langue AfriMed™ prise en charge en staging si nécessaire.",
  "Use clear language names such as Shona, isiNdebele, Zulu, or Portuguese.":
    "Utilisez des noms de langues clairs comme shona, isiNdebele, zoulou ou portugais.",
  "If a language is not supported, Fola may fall back to a safer supported response.":
    "Si une langue n’est pas prise en charge, Fola peut revenir à une réponse plus sûre dans une langue prise en charge.",
  "Treat translations as support guidance, not clinical authority.":
    "Considérez les traductions comme une aide d’accompagnement, pas comme une autorité clinique.",
  "Currently expanding": "En développement",
  "Multilingual support is improving during staging and should not be presented as universal fluency.":
    "Le support multilingue s’améliore pendant staging et ne doit pas être présenté comme une maîtrise universelle.",
  "Language support screenshot slot": "Emplacement de capture du support linguistique",
  "Add a Fola language request screenshot after staging language captures are reviewed.":
    "Ajoutez une capture de demande de langue Fola après revue des captures linguistiques staging.",
  "Open Messages from AfriMed™ navigation.":
    "Ouvrez Messages depuis la navigation AfriMed™.",
  "Select the clinician or conversation thread.":
    "Sélectionnez le clinicien ou le fil de conversation.",
  "Write a clear message with the context your clinician needs.":
    "Rédigez un message clair avec le contexte dont votre clinicien a besoin.",
  "Wait for clinician review before making care decisions.":
    "Attendez la revue du clinicien avant de prendre des décisions de soins.",
  "Clinician-reviewed care": "Soins revus par un clinicien",
  "Messages can support continuity, but urgent or emergency concerns should use the appropriate urgent care channel.":
    "Les messages peuvent soutenir la continuité, mais les urgences doivent passer par le canal de soins urgents approprié.",
  "Messages screenshot slot": "Emplacement de capture des messages",
  "Add the AfriMed™ Messages thread screen once care communication captures are ready.":
    "Ajoutez l’écran de fil Messages AfriMed™ lorsque les captures de communication de soins seront prêtes.",
  "Open Appointments and choose the booking path that matches the care you need.":
    "Ouvrez Rendez-vous et choisissez le parcours de réservation correspondant aux soins dont vous avez besoin.",
  "Search or browse available clinicians, services, locations, or video visit options.":
    "Recherchez ou parcourez les cliniciens, services, lieux ou options de consultation vidéo disponibles.",
  "Review the date, time, clinician, fee, language, and visit type before continuing.":
    "Vérifiez la date, l’heure, le clinicien, les frais, la langue et le type de visite avant de continuer.",
  "Confirm the appointment only when the details match the person receiving care.":
    "Confirmez le rendez-vous uniquement lorsque les détails correspondent à la personne qui reçoit les soins.",
  "Appointment availability can vary by clinician, market, and staging account. If a slot is unavailable, check again later or use support guidance.":
    "Les disponibilités peuvent varier selon le clinicien, le marché et le compte staging. Si un créneau est indisponible, vérifiez plus tard ou utilisez l’assistance.",
  "Appointment booking screenshot slot": "Emplacement de capture de réservation",
  "Add the appointment booking screen once the walkthrough capture set is ready.":
    "Ajoutez l’écran de réservation lorsque l’ensemble de captures du parcours sera prêt.",
  "Open Appointments and choose the upcoming appointment you want to review.":
    "Ouvrez Rendez-vous et choisissez le rendez-vous à venir que vous souhaitez vérifier.",
  "Check the visit type, clinician, location, and payment details before changing anything.":
    "Vérifiez le type de visite, le clinicien, le lieu et les détails de paiement avant toute modification.",
  "Use the available change or cancel action if it is enabled for your account.":
    "Utilisez l’action de modification ou d’annulation disponible si elle est activée pour votre compte.",
  "If the action is not available, contact support or follow the clinic instructions shown in the appointment.":
    "Si l’action n’est pas disponible, contactez l’assistance ou suivez les instructions de la clinique affichées dans le rendez-vous.",
  "Staging limitation": "Limite de staging",
  "Some change and cancellation controls are still being enabled. AfriMed™ should not imply a change is confirmed until the appointment status updates.":
    "Certaines commandes de modification et d’annulation sont encore en cours d’activation. AfriMed™ ne doit pas suggérer qu’un changement est confirmé avant la mise à jour du statut du rendez-vous.",
  "Appointment detail screenshot slot": "Emplacement de capture du détail du rendez-vous",
  "Add the upcoming appointment detail screen when booking management captures are available.":
    "Ajoutez l’écran de détail du rendez-vous à venir lorsque les captures de gestion des réservations seront disponibles.",
  "Open the appointment detail page before the visit.":
    "Ouvrez la page de détail du rendez-vous avant la visite.",
  "Review the appointment time, location or video link, clinician, and reason for visit.":
    "Vérifiez l’heure, le lieu ou le lien vidéo, le clinicien et le motif de la visite.",
  "Prepare any questions, medication names, or documents you want the clinician to review.":
    "Préparez les questions, noms de médicaments ou documents que vous souhaitez faire examiner par le clinicien.",
  "Use emergency services or direct clinician contact for urgent symptoms instead of waiting for a scheduled visit.":
    "Utilisez les services d’urgence ou le contact direct avec un clinicien pour les symptômes urgents au lieu d’attendre une visite planifiée.",
  "Care readiness": "Préparation aux soins",
  "Good preparation helps the visit run smoothly, but urgent clinical concerns should use urgent care channels.":
    "Une bonne préparation facilite la visite, mais les préoccupations cliniques urgentes doivent passer par les canaux de soins urgents.",
  "Visit preparation screenshot slot": "Emplacement de capture de préparation de visite",
  "Add a visit detail or preparation screen once appointment screenshots are captured.":
    "Ajoutez un écran de détail ou de préparation de visite lorsque les captures de rendez-vous seront disponibles.",
  "Open Settings and select your profile row.":
    "Ouvrez les réglages et sélectionnez la ligne de votre profil.",
  "Choose the profile or intro section available for your role.":
    "Choisissez la section profil ou introduction disponible pour votre rôle.",
  "Update your display details and profile photo where supported.":
    "Mettez à jour vos informations affichées et votre photo de profil lorsque cela est pris en charge.",
  "Save, then return to confirm the updated information is shown correctly.":
    "Enregistrez, puis revenez pour confirmer que les informations mises à jour s’affichent correctement.",
  "Use the patient settings/profile screenshot as the first visual reference.":
    "Utilisez la capture des réglages/profil patient comme première référence visuelle.",
  "Available in future updates": "Disponible dans de futures mises à jour",
  "Open a conversation thread.": "Ouvrez un fil de conversation.",
  "Use the Fola summary action when it is available.":
    "Utilisez l’action de résumé Fola lorsqu’elle est disponible.",
  "Review the summary against the original messages.":
    "Vérifiez le résumé par rapport aux messages d’origine.",
  "Ask your clinician if anything care-related is unclear.":
    "Demandez à votre clinicien si un élément lié aux soins n’est pas clair.",
  "Open the relevant thread.": "Ouvrez le fil concerné.",
  "Ask Fola to help draft calm wording for your message.":
    "Demandez à Fola de vous aider à rédiger un message calme.",
  "Edit the draft so it accurately reflects your situation.":
    "Modifiez le brouillon afin qu’il reflète fidèlement votre situation.",
  "Send only when you are comfortable with the final wording.":
    "Envoyez uniquement lorsque le texte final vous convient.",
  "You stay in control": "Vous gardez le contrôle",
  "Fola can help draft language, but you review and send messages yourself.":
    "Fola peut aider à rédiger le texte, mais vous relisez et envoyez les messages vous-même.",
  "Open the prescription or message context where explanation support is available.":
    "Ouvrez le contexte de prescription ou de message où l’aide aux explications est disponible.",
  "Ask Fola for a plain-language explanation.":
    "Demandez à Fola une explication en langage simple.",
  "Check dose, timing, and warnings against the prescription label.":
    "Vérifiez la dose, l’horaire et les avertissements par rapport à l’étiquette de la prescription.",
  "Do not change medication from Fola alone": "Ne modifiez pas un médicament sur la seule base de Fola",
  "Medication explanations are for understanding. Treatment changes must be clinician or pharmacist reviewed.":
    "Les explications sur les médicaments servent à comprendre. Toute modification de traitement doit être revue par un clinicien ou un pharmacien.",
  "Use Fola for workflow guidance, summaries, drafts, and plain-language support.":
    "Utilisez Fola pour l’aide aux parcours, les résumés, les brouillons et l’accompagnement en langage simple.",
  "Treat Fola as a helper inside AfriMed™, not a replacement for care.":
    "Considérez Fola comme une aide dans AfriMed™, pas comme un remplacement des soins.",
  "Use clinicians, pharmacists, and emergency services for medical decisions.":
    "Utilisez les cliniciens, pharmaciens et services d’urgence pour les décisions médicales.",
  "Report confusing or unsafe Fola output through support.":
    "Signalez à l’assistance toute réponse Fola confuse ou dangereuse.",
  "Honest beta language": "Langage bêta honnête",
  "Fola features are still being refined during staging. AfriMed™ will keep the experience clear about what is live and what is still expanding.":
    "Les fonctionnalités Fola sont encore affinées pendant staging. AfriMed™ gardera l’expérience claire sur ce qui est actif et ce qui est encore en développement.",
  "Open your profile or dependants area.":
    "Ouvrez votre profil ou l’espace personnes à charge.",
  "Choose the option to add a dependant when it is available.":
    "Choisissez l’option d’ajout d’une personne à charge lorsqu’elle est disponible.",
  "Enter the dependant details requested by AfriMed™.":
    "Saisissez les informations de la personne à charge demandées par AfriMed™.",
  "Review the information carefully before using it for appointments or records.":
    "Vérifiez soigneusement les informations avant de les utiliser pour des rendez-vous ou des dossiers.",
  "Dependant profiles are guardian-managed during staging. Separate dependant account handoff and consent flows are future updates.":
    "Les profils de personnes à charge sont gérés par un responsable pendant staging. Le relais vers un compte séparé et les parcours de consentement sont prévus pour de futures mises à jour.",
  "Add dependant screenshot slot": "Emplacement de capture d’ajout de personne à charge",
  "Add the dependant creation screen once guardian workflow captures are ready.":
    "Ajoutez l’écran de création de personne à charge lorsque les captures du parcours responsable seront prêtes.",
  "Start an appointment booking from the appointment area.":
    "Commencez une réservation depuis l’espace rendez-vous.",
  "Select the dependant who will receive care when the selector is available.":
    "Sélectionnez la personne à charge qui recevra les soins lorsque le sélecteur est disponible.",
  "Review clinician, date, time, visit type, and payment details for that dependant.":
    "Vérifiez le clinicien, la date, l’heure, le type de visite et les détails de paiement pour cette personne à charge.",
  "Confirm only after checking that the appointment is not attached to the wrong profile.":
    "Confirmez uniquement après avoir vérifié que le rendez-vous n’est pas rattaché au mauvais profil.",
  "Family care context": "Contexte des soins familiaux",
  "Dependant booking helps coordinate care, but each appointment should clearly show who the visit is for.":
    "La réservation pour une personne à charge aide à coordonner les soins, mais chaque rendez-vous doit montrer clairement pour qui la visite est prévue.",
  "Dependant booking screenshot slot": "Emplacement de capture de réservation pour personne à charge",
  "Add the dependant selector or dependant appointment screen once captured.":
    "Ajoutez le sélecteur de personne à charge ou l’écran de rendez-vous associé une fois capturé.",
  "Use only your own AfriMed™ account to manage dependant care.":
    "Utilisez uniquement votre propre compte AfriMed™ pour gérer les soins d’une personne à charge.",
  "Review dependant details before sharing or confirming care actions.":
    "Vérifiez les informations de la personne à charge avant de partager ou confirmer des actions de soins.",
  "Avoid sending screenshots that expose a dependant's health information.":
    "Évitez d’envoyer des captures qui exposent les informations de santé d’une personne à charge.",
  "Use support if dependant information appears in the wrong account or context.":
    "Utilisez l’assistance si les informations d’une personne à charge apparaissent dans le mauvais compte ou contexte.",
  "Advanced dependant privacy handoff, age-based transitions, and separate dependant access controls are still being expanded.":
    "Le relais avancé de confidentialité des personnes à charge, les transitions liées à l’âge et les contrôles d’accès séparés sont encore en développement.",
  "Dependant privacy screenshot slot": "Emplacement de capture de confidentialité des personnes à charge",
  "Add privacy and dependant access screens after the handoff model is finalized.":
    "Ajoutez les écrans de confidentialité et d’accès des personnes à charge après finalisation du modèle de relais.",
  "Open the upcoming appointment before the scheduled time.":
    "Ouvrez le rendez-vous à venir avant l’heure prévue.",
  "Check whether the appointment is marked as a video consultation.":
    "Vérifiez si le rendez-vous est indiqué comme consultation vidéo.",
  "Use the join action when it becomes available.":
    "Utilisez l’action de rejoindre lorsqu’elle devient disponible.",
  "Keep your device charged and stay somewhere private for the consultation.":
    "Gardez votre appareil chargé et installez-vous dans un endroit privé pour la consultation.",
  "Video join controls may only appear for supported appointment types and enabled staging accounts.":
    "Les commandes de connexion vidéo peuvent apparaître uniquement pour les types de rendez-vous pris en charge et les comptes staging activés.",
  "Video consultation screenshot slot": "Emplacement de capture de consultation vidéo",
  "Add the video appointment screen when remote visit captures are ready.":
    "Ajoutez l’écran de rendez-vous vidéo lorsque les captures de visite à distance seront prêtes.",
  "Open AfriMed™ on a device with a stable internet connection.":
    "Ouvrez AfriMed™ sur un appareil avec une connexion internet stable.",
  "Allow camera and microphone permissions when prompted by your browser.":
    "Autorisez la caméra et le micro lorsque votre navigateur vous le demande.",
  "Check that headphones, camera, and microphone work before the visit.":
    "Vérifiez que les écouteurs, la caméra et le micro fonctionnent avant la visite.",
  "Use support or the appointment instructions if the video action does not appear.":
    "Utilisez l’assistance ou les instructions du rendez-vous si l’action vidéo n’apparaît pas.",
  "Private remote care": "Soins à distance privés",
  "Choose a private location for video visits so health details are not exposed to people nearby.":
    "Choisissez un lieu privé pour les visites vidéo afin que les informations de santé ne soient pas exposées aux personnes autour de vous.",
  "Video access screenshot slot": "Emplacement de capture de l’accès vidéo",
  "Add browser permission and video readiness screens after capture.":
    "Ajoutez les écrans d’autorisation du navigateur et de préparation vidéo après capture.",
  "Use a strong password that you do not reuse on other services.":
    "Utilisez un mot de passe fort que vous ne réutilisez pas sur d’autres services.",
  "Keep your email and phone details current so recovery messages reach you.":
    "Gardez votre email et votre téléphone à jour afin que les messages de récupération vous parviennent.",
  "Sign out on shared devices after using AfriMed™.":
    "Déconnectez-vous des appareils partagés après avoir utilisé AfriMed™.",
  "Contact support if you notice access you do not recognize.":
    "Contactez l’assistance si vous remarquez un accès que vous ne reconnaissez pas.",
  "Private healthcare access": "Accès privé aux soins",
  "AfriMed™ account security protects care messages, appointment details, and health information.":
    "La sécurité du compte AfriMed™ protège les messages de soins, les détails de rendez-vous et les informations de santé.",
  "Security settings screenshot slot": "Emplacement de capture des réglages de sécurité",
  "Add account security screens once the settings walkthrough is captured.":
    "Ajoutez les écrans de sécurité du compte une fois le parcours des réglages capturé.",
  "Use authenticated AfriMed™ screens for healthcare actions.":
    "Utilisez les écrans AfriMed™ authentifiés pour les actions de santé.",
  "Avoid sharing screenshots that show health records, prescriptions, or private messages.":
    "Évitez de partager des captures qui montrent des dossiers de santé, des prescriptions ou des messages privés.",
  "Review the account context before opening dependant or clinician-linked information.":
    "Vérifiez le contexte du compte avant d’ouvrir les informations liées à une personne à charge ou à un clinicien.",
  "Use support if private information appears in the wrong place.":
    "Utilisez l’assistance si des informations privées apparaissent au mauvais endroit.",
  "Context matters": "Le contexte compte",
  "AfriMed™ is designed to keep healthcare information attached to the right account, role, and care relationship.":
    "AfriMed™ est conçu pour garder les informations de santé rattachées au bon compte, au bon rôle et à la bonne relation de soins.",
  "Privacy guidance screenshot slot": "Emplacement de capture des conseils de confidentialité",
  "Add privacy and account context screens after approved captures are ready.":
    "Ajoutez les écrans de confidentialité et de contexte du compte lorsque les captures approuvées seront prêtes.",
  "Choose Doctor or Clinician when AfriMed™ asks how you want to use the app.":
    "Choisissez Médecin ou Clinicien lorsque AfriMed™ vous demande comment vous souhaitez utiliser l’application.",
  "Complete the shared intro steps using your own name, phone number, pass code, date of birth, and account details.":
    "Terminez les étapes d’introduction partagées avec votre propre nom, numéro de téléphone, code d’accès, date de naissance et informations de compte.",
  "Continue through doctor setup, including ID/passport, languages, specialty, registration, formation, registration details, and About.":
    "Continuez la configuration médecin, y compris pièce d’identité/passeport, langues, spécialité, inscription, formation, détails d’inscription et À propos.",
  "Use Go to settings at completion so your clinician profile can be reviewed before operational use.":
    "Utilisez Aller aux réglages à la fin afin de revoir votre profil clinicien avant une utilisation opérationnelle.",
  "Clinician context": "Contexte clinicien",
  "Doctor onboarding should save canonical doctor profile details. It should not hydrate from an old patient session or local draft after completion.":
    "L’inscription médecin doit enregistrer les informations canoniques du profil médecin. Elle ne doit pas se remplir depuis une ancienne session patient ou un brouillon local après finalisation.",
  "Choose clinician, then confirm your name": "Choisissez clinicien, puis confirmez votre nom",
  "Select the clinician role first, then enter the legal names that match your official ID.":
    "Sélectionnez d’abord le rôle clinicien, puis saisissez les noms légaux correspondant à votre pièce d’identité officielle.",
  "Choose Register with email when creating your clinician account.":
    "Choisissez S’inscrire avec l’email lors de la création de votre compte clinicien.",
  "Staging access is private. Use the invited email address so the account stays attached to the right tester and clinician context.":
    "L’accès staging est privé. Utilisez l’adresse email invitée afin que le compte reste associé au bon testeur et au bon contexte clinicien.",
  "Choose Clinician when AfriMed™ asks how you want to use the app.":
    "Choisissez Clinicien lorsque AfriMed™ vous demande comment vous souhaitez utiliser l’application.",
  "Avoid continuing as Patient if you are setting up a doctor account.":
    "Évitez de continuer comme Patient si vous configurez un compte médecin.",
  "Clinician role context": "Contexte du rôle clinicien",
  "The clinician role keeps doctor onboarding, settings, and profile data separate from patient account information.":
    "Le rôle clinicien sépare l’inscription médecin, les réglages et les données de profil des informations de compte patient.",
  "Select Clinician first, then enter names that match your official identification.":
    "Sélectionnez d’abord Clinicien, puis saisissez les noms correspondant à votre pièce d’identité officielle.",
  "Enter the phone number you want attached to your clinician account.":
    "Saisissez le numéro de téléphone que vous souhaitez associer à votre compte clinicien.",
  "Avoid using a passcode that colleagues, staff, or family can guess.":
    "Évitez d’utiliser un code d’accès que des collègues, le personnel ou la famille pourraient deviner.",
  "Continue into clinician-specific onboarding rather than stopping at the intro completion screen.":
    "Continuez vers l’inscription propre au clinicien au lieu de vous arrêter à l’écran de fin d’introduction.",
  "Use a staging bypass only when the screen explicitly offers it.":
    "Utilisez un contournement staging uniquement lorsque l’écran le propose explicitement.",
  "Treat bypassed identity review as pending until real verification is completed.":
    "Considérez toute revue d’identité contournée comme en attente jusqu’à la fin de la vérification réelle.",
  "No fake verification": "Aucune fausse vérification",
  "A staging bypass must not imply government, ID, passport, or document verification unless that verification actually happened.":
    "Un contournement staging ne doit pas impliquer une vérification gouvernementale, d’identité, de passeport ou de document sauf si cette vérification a réellement eu lieu.",
  "ID, passport, consent, and OCR": "Pièce d’identité, passeport, consentement et OCR",
  "Complete the identity screens truthfully; staging bypass means pending review, not verified identity.":
    "Renseignez les écrans d’identité avec exactitude ; le contournement staging signifie revue en attente, pas identité vérifiée.",
  "Read any consent or OCR prompt before continuing.":
    "Lisez toute demande de consentement ou d’OCR avant de continuer.",
  "Choose the languages you can comfortably use for care communication.":
    "Choisissez les langues que vous pouvez utiliser confortablement pour la communication de soins.",
  "Select your primary specialty from the supported list.":
    "Sélectionnez votre spécialité principale dans la liste prise en charge.",
  "Add subspecialty information when the next screen asks for it.":
    "Ajoutez les informations de sous-spécialité lorsque l’écran suivant les demande.",
  "Use the closest accurate match if the exact label is still being expanded during staging.":
    "Utilisez l’option exacte la plus proche si le libellé précis est encore en développement pendant staging.",
  "Review these fields later in doctor Settings when needed.":
    "Revoyez ces champs plus tard dans les réglages médecin si nécessaire.",
  "Languages and specialty": "Langues et spécialité",
  "Select care languages and your primary specialty before continuing into subspecialty setup.":
    "Sélectionnez les langues de soins et votre spécialité principale avant de continuer vers la configuration des sous-spécialités.",
  "Complete the subspecialty screen with accurate professional context.":
    "Terminez l’écran de sous-spécialité avec un contexte professionnel exact.",
  "Upload the registration document or image requested by the screen.":
    "Téléversez le document ou l’image d’inscription demandé par l’écran.",
  "Use a clear file that matches your professional registration details.":
    "Utilisez un fichier clair correspondant à vos informations d’inscription professionnelle.",
  "Remember that upload is not the same as completed verification.":
    "Rappelez-vous que le téléversement n’est pas une vérification terminée.",
  "Continue once the file is attached or the staging-safe path is shown.":
    "Continuez une fois le fichier joint ou lorsque le parcours sûr staging est affiché.",
  "Upload is review input": "Le téléversement sert à la revue",
  "A registration upload gives AfriMed™ material to review. It does not by itself mark professional registration as verified.":
    "Un téléversement d’inscription fournit à AfriMed™ des éléments à examiner. Il ne marque pas à lui seul l’inscription professionnelle comme vérifiée.",
  "Add formation or training details requested by the onboarding screen.":
    "Ajoutez les informations de formation demandées par l’écran d’inscription.",
  "Enter registration details exactly as they appear in your professional records.":
    "Saisissez les informations d’inscription exactement comme elles apparaissent dans vos dossiers professionnels.",
  "Check spelling, registration numbers, and dates before continuing.":
    "Vérifiez l’orthographe, les numéros d’inscription et les dates avant de continuer.",
  "Leave unsupported claims out of the profile until AfriMed™ supports verified review for them.":
    "Laissez les affirmations non prises en charge hors du profil jusqu’à ce qu’AfriMed™ permette une revue vérifiée.",
  "Add a short About section that describes your clinical practice clearly.":
    "Ajoutez une courte section À propos qui décrit clairement votre pratique clinique.",
  "Keep the tone professional, understandable, and patient-facing.":
    "Gardez un ton professionnel, compréhensible et adapté aux patients.",
  "Use Fola drafting support only as a writing aid.":
    "Utilisez l’aide de rédaction Fola uniquement comme aide à l’écriture.",
  "Review and edit any generated text before saving it to your clinician profile.":
    "Relisez et modifiez tout texte généré avant de l’enregistrer dans votre profil clinicien.",
  "Avoid unsupported claims, exaggerated credentials, or unverified specialties.":
    "Évitez les affirmations non prises en charge, les qualifications exagérées ou les spécialités non vérifiées.",
  "You approve the bio": "Vous approuvez la bio",
  "Fola can help draft wording, but the final About text should be reviewed and approved by the clinician.":
    "Fola peut aider à rédiger, mais le texte final À propos doit être relu et approuvé par le clinicien.",
  "Complete the final onboarding screen after all required clinician steps are done.":
    "Terminez le dernier écran d’inscription une fois toutes les étapes clinicien requises terminées.",
  "Check the Settings welcome screen for the correct doctor context.":
    "Vérifiez que l’écran d’accueil des réglages affiche le bon contexte médecin.",
  "Open doctor Settings and review profile, verification, and practice details.":
    "Ouvrez les réglages médecin et vérifiez le profil, la vérification et les détails de pratique.",
  "Refresh or sign in again later to confirm the profile still hydrates from saved doctor data.":
    "Actualisez ou reconnectez-vous plus tard pour confirmer que le profil se remplit toujours depuis les données médecin enregistrées.",
  "Review before operational use": "Revue avant utilisation opérationnelle",
  "Settings is where the clinician profile can be reviewed before wider patient-facing or operational use.":
    "Les réglages permettent de vérifier le profil clinicien avant une utilisation plus large visible par les patients ou opérationnelle.",
  "Open doctor Settings and find practice or facility setup.":
    "Ouvrez les réglages médecin et trouvez la configuration du cabinet ou de l’établissement.",
  "Review facility name, address, contact details, and consultation context.":
    "Vérifiez le nom de l’établissement, l’adresse, les coordonnées et le contexte de consultation.",
  "Save only facilities you are authorized to use.":
    "Enregistrez uniquement les établissements que vous êtes autorisé à utiliser.",
  "Check that booking or profile screens show the intended practice information.":
    "Vérifiez que les écrans de réservation ou de profil affichent les informations de pratique prévues.",
  "Open services or consultancies in doctor Settings.":
    "Ouvrez les services ou consultations dans les réglages médecin.",
  "Add supported consultation types, duration, mode, and price where enabled.":
    "Ajoutez les types de consultation, durée, mode et prix pris en charge lorsque c’est activé.",
  "Review how each service appears before making it bookable.":
    "Vérifiez l’affichage de chaque service avant de le rendre réservable.",
  "Use staging copy as pending if payments or booking controls are still being expanded.":
    "Présentez le contenu staging comme en attente si les paiements ou commandes de réservation sont encore en développement.",
  "Open the availability area in doctor Settings.":
    "Ouvrez la section des disponibilités dans les réglages médecin.",
  "Review working hours, blocked times, and consultation modes.":
    "Vérifiez les heures de travail, les plages bloquées et les modes de consultation.",
  "Save changes and check appointment screens for the expected slots.":
    "Enregistrez les changements et vérifiez les créneaux attendus sur les écrans de rendez-vous.",
  "Do not treat unavailable or hidden slots as confirmed bookings.":
    "Ne considérez pas les créneaux indisponibles ou masqués comme des réservations confirmées.",
  "Open Doctor Settings after onboarding completion.":
    "Ouvrez les réglages médecin après la fin de l’inscription.",
  "Review name, professional display details, profile photo, and About information.":
    "Vérifiez le nom, les informations professionnelles affichées, la photo de profil et les informations À propos.",
  "Update patient-facing details only from the doctor profile area.":
    "Mettez à jour les informations visibles par les patients uniquement depuis l’espace profil médecin.",
  "Refresh or sign in again to confirm the profile hydrates from saved doctor data.":
    "Actualisez ou reconnectez-vous pour confirmer que le profil se remplit depuis les données médecin enregistrées.",
  "Doctor settings screenshot slot": "Emplacement de capture des réglages médecin",
  "Add doctor settings and profile screenshots after the staging doctor run.":
    "Ajoutez les captures des réglages et du profil médecin après le parcours médecin staging.",
  "Open the specialty area during onboarding or doctor Settings.":
    "Ouvrez l’espace spécialité pendant l’inscription ou dans les réglages médecin.",
  "Select your primary specialty and any supported subspecialty fields.":
    "Sélectionnez votre spécialité principale et les champs de sous-spécialité pris en charge.",
  "Save changes and check that patient-facing discovery uses the same values.":
    "Enregistrez les changements et vérifiez que la découverte visible par les patients utilise les mêmes valeurs.",
  "Use support if specialty information disappears after refresh.":
    "Utilisez l’assistance si les informations de spécialité disparaissent après actualisation.",
  "Open doctor profile or onboarding language selection.":
    "Ouvrez le profil médecin ou la sélection de langue pendant l’inscription.",
  "Choose the languages you can confidently use with patients.":
    "Choisissez les langues que vous pouvez utiliser avec confiance avec les patients.",
  "Save the profile and review the visible doctor summary.":
    "Enregistrez le profil et vérifiez le résumé médecin visible.",
  "Update languages when your practice support changes.":
    "Mettez à jour les langues lorsque le support de votre pratique change.",
  "Enter ID/passport details only in the doctor verification step.":
    "Saisissez les informations de pièce d’identité/passeport uniquement dans l’étape de vérification médecin.",
  "Add registration or license fields where AfriMed™ asks for them.":
    "Ajoutez les champs d’inscription ou de licence lorsque AfriMed™ les demande.",
  "Use staging bypass only as pending verification.":
    "Utilisez le contournement staging uniquement comme vérification en attente.",
  "Check Settings after completion to confirm the saved values remain attached to the doctor profile.":
    "Vérifiez les réglages après finalisation pour confirmer que les valeurs enregistrées restent rattachées au profil médecin.",
  "Pending means pending": "En attente signifie en attente",
  "Saved doctor ID, passport, and registration details are not government or council verified until AfriMed™ performs that verification.":
    "Les informations médecin enregistrées de pièce d’identité, passeport et inscription ne sont pas vérifiées par une autorité ou un conseil professionnel tant qu’AfriMed™ n’a pas effectué cette vérification.",
  "Open the formation or credentials step during doctor onboarding.":
    "Ouvrez l’étape de formation ou de justificatifs pendant l’inscription médecin.",
  "Enter qualifications and registration details requested by AfriMed™.":
    "Saisissez les qualifications et informations d’inscription demandées par AfriMed™.",
  "Upload documents only where a secure upload action is shown.":
    "Téléversez des documents uniquement lorsqu’une action de téléversement sécurisé est affichée.",
  "Confirm the status remains pending when review has not happened yet.":
    "Confirmez que le statut reste en attente lorsque la revue n’a pas encore eu lieu.",
  "AfriMed™ uses Supabase infrastructure with TLS encryption in transit and AES-256 encryption at rest.":
    "AfriMed™ utilise l’infrastructure Supabase avec chiffrement TLS en transit et chiffrement AES-256 au repos.",
  "Supabase maintains SOC 2 Type 2 and ISO 27001 controls for its managed infrastructure.":
    "Supabase maintient des contrôles SOC 2 Type 2 et ISO 27001 pour son infrastructure gérée.",
  "Uploaded registration documents are used for verification review and remain pending until AfriMed™ completes that review.":
    "Les documents d’inscription téléversés servent à la revue de vérification et restent en attente jusqu’à ce qu’AfriMed™ termine cette revue.",
  "A pending or uploaded credential does not mean the clinician is verified, approved for discovery, or cleared for regulated clinical activity.":
    "Un justificatif en attente ou téléversé ne signifie pas que le clinicien est vérifié, approuvé pour la découverte ou autorisé pour une activité clinique réglementée.",
  "Security language matters": "Le langage de sécurité compte",
  "AfriMed™ should not be described as HIPAA certified, formally audited, or impossible to hack unless those legal and technical audits have been completed.":
    "AfriMed™ ne doit pas être décrit comme certifié HIPAA, formellement audité ou impossible à pirater tant que ces audits juridiques et techniques n’ont pas été réalisés.",
  "Open the doctor home or appointments area.":
    "Ouvrez l’accueil médecin ou l’espace rendez-vous.",
  "Review patient name, appointment time, visit type, service, and status.":
    "Vérifiez le nom du patient, l’heure du rendez-vous, le type de visite, le service et le statut.",
  "Open the appointment detail before the visit.":
    "Ouvrez le détail du rendez-vous avant la visite.",
  "Use patient health information only within the proper care context.":
    "Utilisez les informations de santé du patient uniquement dans le contexte de soins approprié.",
  "Open the appointment detail.": "Ouvrez le détail du rendez-vous.",
  "Use available status, reschedule, or cancellation actions only when enabled.":
    "Utilisez les actions de statut, reprogrammation ou annulation disponibles uniquement lorsqu’elles sont activées.",
  "Wait for the updated status to appear before treating a change as complete.":
    "Attendez que le statut mis à jour apparaisse avant de considérer un changement comme terminé.",
  "Use support if the UI and appointment truth do not match.":
    "Utilisez l’assistance si l’interface et l’état réel du rendez-vous ne correspondent pas.",
  "Some appointment controls are still expanding. AfriMed™ should not imply a status change has happened until the saved appointment state updates.":
    "Certaines commandes de rendez-vous sont encore en développement. AfriMed™ ne doit pas suggérer qu’un changement de statut a eu lieu tant que l’état enregistré du rendez-vous n’est pas mis à jour.",
  "Open Messages from doctor navigation.":
    "Ouvrez Messages depuis la navigation médecin.",
  "Select the patient or appointment-linked thread.":
    "Sélectionnez le patient ou le fil lié au rendez-vous.",
  "Write clinically clear messages and review before sending.":
    "Rédigez des messages cliniquement clairs et relisez-les avant l’envoi.",
  "Use urgent care channels for emergencies instead of ordinary message threads.":
    "Utilisez les canaux de soins urgents pour les urgences plutôt que les fils de messages ordinaires.",
  "Open the patient or appointment context where EHR access is available.":
    "Ouvrez le contexte patient ou rendez-vous où l’accès au DME est disponible.",
  "Request access with a clear reason tied to care.":
    "Demandez l’accès avec une raison claire liée aux soins.",
  "Wait for approval or the available consent state before reviewing records.":
    "Attendez l’approbation ou l’état de consentement disponible avant de consulter les dossiers.",
  "Use only the minimum patient information needed for care.":
    "N’utilisez que le minimum d’informations patient nécessaire aux soins.",
  "Consent and context": "Consentement et contexte",
  "Doctor EHR access should be tied to the right patient, appointment, consent, or care relationship.":
    "L’accès médecin au DME doit être lié au bon patient, rendez-vous, consentement ou relation de soins.",
  "Open the referral action from the appointment or patient context.":
    "Ouvrez l’action d’orientation depuis le contexte du rendez-vous ou du patient.",
  "Check patient, receiving clinician or facility, reason, and attachments.":
    "Vérifiez le patient, le clinicien ou l’établissement destinataire, le motif et les pièces jointes.",
  "Send only when the referral details are correct.":
    "Envoyez uniquement lorsque les détails de l’orientation sont corrects.",
  "Use support if referral visibility appears in the wrong role or account.":
    "Utilisez l’assistance si la visibilité de l’orientation apparaît dans le mauvais rôle ou compte.",
  "Open the appointment or patient context for the prescription.":
    "Ouvrez le contexte de rendez-vous ou de patient pour la prescription.",
  "Check patient identity, medicine, dose, duration, instructions, and facility context.":
    "Vérifiez l’identité du patient, le médicament, la dose, la durée, les instructions et le contexte d’établissement.",
  "Review the prescription before issuing it.":
    "Relisez la prescription avant de l’émettre.",
  "Confirm the saved prescription status before telling the patient it is available.":
    "Confirmez le statut enregistré de la prescription avant d’indiquer au patient qu’elle est disponible.",
  "Clinician responsibility": "Responsabilité du clinicien",
  "Prescription decisions remain the responsibility of the qualified clinician.":
    "Les décisions de prescription restent sous la responsabilité du clinicien qualifié.",
  "Open a supported message or appointment context.":
    "Ouvrez un contexte de message ou de rendez-vous pris en charge.",
  "Use Fola for summaries, draft replies, or plain-language structure where available.":
    "Utilisez Fola pour les résumés, brouillons de réponse ou structures en langage simple lorsque disponibles.",
  "Review every generated output before using it clinically.":
    "Relisez chaque sortie générée avant toute utilisation clinique.",
  "Do not let Fola diagnose, prescribe, or replace your professional judgement.":
    "Ne laissez pas Fola diagnostiquer, prescrire ou remplacer votre jugement professionnel.",
  "Review required": "Revue obligatoire",
  "Fola can support clinician workflows, but clinician review remains required for clinical notes, prescriptions, and patient communication.":
    "Fola peut soutenir les parcours cliniciens, mais la revue du clinicien reste obligatoire pour les notes cliniques, les prescriptions et la communication patient.",
  "Open the consultation or note context where note support is enabled.":
    "Ouvrez le contexte de consultation ou de note où l’aide aux notes est activée.",
  "Review any generated structure against the consultation facts.":
    "Vérifiez toute structure générée par rapport aux faits de la consultation.",
  "Edit the note until it reflects your clinical judgement.":
    "Modifiez la note jusqu’à ce qu’elle reflète votre jugement clinique.",
  "Save only after checking patient, appointment, and note context.":
    "Enregistrez uniquement après vérification du patient, du rendez-vous et du contexte de note.",
  "VitalRep and SOAP note support may not be available to every staging account yet.":
    "Le support VitalRep et notes SOAP peut ne pas encore être disponible pour tous les comptes staging.",
  "Use your doctor account only for clinician workflows.":
    "Utilisez votre compte médecin uniquement pour les parcours cliniciens.",
  "Confirm patient and appointment context before opening EHR, prescriptions, referrals, or messages.":
    "Confirmez le contexte patient et rendez-vous avant d’ouvrir le DME, les prescriptions, les orientations ou les messages.",
  "Avoid sharing screenshots that expose patient data.":
    "Évitez de partager des captures qui exposent des données patient.",
  "Report any patient data appearing in the wrong account, role, or screen.":
    "Signalez toute donnée patient apparaissant dans le mauvais compte, rôle ou écran.",
  "Role-aware access": "Accès conscient des rôles",
  "AfriMed™ must keep patient and doctor information attached to the right authenticated role and care relationship.":
    "AfriMed™ doit maintenir les informations patient et médecin rattachées au bon rôle authentifié et à la bonne relation de soins.",
  "Open AfriMed™ on a current desktop browser.":
    "Ouvrez AfriMed™ dans un navigateur desktop à jour.",
  "Use the doctor dashboard for appointment and workflow scanning.":
    "Utilisez le tableau de bord médecin pour parcourir les rendez-vous et les flux de travail.",
  "Open Settings for profile, verification, services, availability, and practice setup.":
    "Ouvrez les réglages pour le profil, la vérification, les services, les disponibilités et la configuration de pratique.",
  "Sign out on shared or clinic devices when finished.":
    "Déconnectez-vous sur les appareils partagés ou de clinique lorsque vous avez terminé.",
  "Use the invited pharmacy email to sign in through AfriMed™ staging.":
    "Utilisez l’email pharmacie invité pour vous connecter à AfriMed™ staging.",
  "Select Pharmacy when AfriMed™ asks how you want to use the app.":
    "Sélectionnez Pharmacie lorsque AfriMed™ vous demande comment vous souhaitez utiliser l’application.",
  "Complete the low-friction pharmacy onboarding path for staging.":
    "Terminez le parcours d’onboarding pharmacie simplifié pour staging.",
  "Launch the Pharmacy Dashboard once verification and payout are marked pending for staging.":
    "Lancez le tableau de bord pharmacie une fois la vérification et le paiement marqués en attente pour staging.",
  "Use the seeded stock, prescription requests, payments, messages, and Fola™ prompts to test the branch workflow.":
    "Utilisez le stock préchargé, les demandes de prescription, les paiements, les messages et les prompts Fola™ pour tester le parcours de l’officine.",
  "Staging data is deliberate": "Les données staging sont délibérées",
  "New staging pharmacy branches receive demo stock and sample fulfilment requests so testers can explore the dashboard without real patient, payment, or prescription data.":
    "Les nouvelles officines staging reçoivent du stock de démonstration et des demandes d’exécution exemples afin que les testeurs explorent le tableau de bord sans données réelles de patient, paiement ou prescription.",
  "Pharmacy staging entry": "Entrée pharmacie staging",
  "Use the invited pharmacy email and continue into the pharmacy onboarding route.":
    "Utilisez l’email pharmacie invité et continuez vers le parcours d’onboarding pharmacie.",
  "Dashboard and inventory preview": "Aperçu tableau de bord et inventaire",
  "After launch, seeded demo rows make the dashboard and inventory understandable immediately.":
    "Après le lancement, les lignes de démonstration préchargées rendent le tableau de bord et l’inventaire immédiatement compréhensibles.",
  "Begin with the pharmacy identity step and add available branch details.":
    "Commencez par l’étape d’identité pharmacie et ajoutez les informations d’officine disponibles.",
  "Use staging-safe placeholders where fields are not available yet.":
    "Utilisez des valeurs temporaires sûres pour staging lorsque les champs ne sont pas encore disponibles.",
  "Continue through operations, fulfilment, verification, and role handoff steps.":
    "Continuez les étapes d’opérations, d’exécution, de vérification et de relais de rôle.",
  "Leave verification and payout readiness pending when real documents or Stripe setup are not part of the staging test.":
    "Laissez la vérification et la préparation des paiements en attente lorsque les documents réels ou la configuration Stripe ne font pas partie du test staging.",
  "Launch the Pharmacy Dashboard and review branch Settings after completion.":
    "Lancez le tableau de bord pharmacie et vérifiez les réglages de l’officine après finalisation.",
  "Pending does not mean production approved": "En attente ne signifie pas approuvé pour la production",
  "Staging access allows testers into the dashboard. Live prescription routing, public branch availability, payouts, and production fulfilment still require verification and AfriMed™ approval.":
    "L’accès staging permet aux testeurs d’entrer dans le tableau de bord. Le routage réel des prescriptions, la disponibilité publique de l’officine, les paiements et l’exécution production nécessitent encore vérification et approbation AfriMed™.",
  "Onboarding steps 1 to 3": "Étapes d’onboarding 1 à 3",
  "Capture branch identity, operation basics, and staging verification status.":
    "Renseignez l’identité de l’officine, les bases opérationnelles et le statut de vérification staging.",
  "Onboarding steps 4 to 5": "Étapes d’onboarding 4 à 5",
  "Review role handoff, pending readiness, and the launch step before opening the dashboard.":
    "Vérifiez le relais de rôle, la préparation en attente et l’étape de lancement avant d’ouvrir le tableau de bord.",
  "Pharmacy account": "Compte pharmacie",
  "Use the account area to review branch identity and workspace preferences after launch.":
    "Utilisez l’espace compte pour vérifier l’identité de l’officine et les préférences de l’espace de travail après lancement.",
  "Open Inventory from the pharmacy header.":
    "Ouvrez Inventaire depuis l’en-tête pharmacie.",
  "Confirm that seeded demo stock appears before adding real test stock.":
    "Confirmez que le stock de démonstration préchargé apparaît avant d’ajouter du stock test réel.",
  "Use Add stock item to create a new branch-specific catalogue row.":
    "Utilisez Ajouter un article de stock pour créer une nouvelle ligne catalogue propre à l’officine.",
  "Check low-stock, cold-chain, on-hand, reorder, price, and status fields.":
    "Vérifiez les champs stock bas, chaîne du froid, disponible, réapprovisionnement, prix et statut.",
  "Report any disappearing rows or image flicker as a staging hydration issue.":
    "Signalez toute ligne qui disparaît ou tout scintillement d’image comme problème d’hydratation staging.",
  "Branch-scoped stock": "Stock limité à l’officine",
  "Inventory rows must remain tied to the current pharmacy branch. Demo rows should not appear across unrelated branches.":
    "Les lignes d’inventaire doivent rester liées à l’officine actuelle. Les lignes de démonstration ne doivent pas apparaître dans des officines non liées.",
  "Open the prescription queue and choose a seeded fulfilment request.":
    "Ouvrez la file de prescriptions et choisissez une demande d’exécution préchargée.",
  "Verify the prescription details and keep doctor-issued prescribing separate from pharmacy fulfilment.":
    "Vérifiez les détails de prescription et gardez la prescription émise par le médecin séparée de l’exécution pharmacie.",
  "Confirm stock and price using branch inventory and safe staging values.":
    "Confirmez le stock et le prix à partir de l’inventaire de l’officine et de valeurs staging sûres.",
  "Move the request through preparation, verification, dispensing, or handoff steps where available.":
    "Faites avancer la demande par les étapes de préparation, vérification, délivrance ou remise lorsque disponibles.",
  "Use the handoff notes to explain what the patient or support team should see next.":
    "Utilisez les notes de remise pour expliquer ce que le patient ou l’équipe d’assistance doit voir ensuite.",
  "Do not override doctors": "Ne pas remplacer les médecins",
  "Pharmacy fulfilment supports preparation and dispensing. It must not diagnose patients, alter doctor prescribing, or route fulfilment unfairly.":
    "L’exécution pharmacie soutient la préparation et la délivrance. Elle ne doit pas diagnostiquer les patients, modifier les prescriptions médicales ou orienter l’exécution de manière injuste.",
  "Open Payments to review staging transaction rows.":
    "Ouvrez Paiements pour consulter les lignes de transaction staging.",
  "Confirm that paid demo items contribute to dashboard revenue.":
    "Confirmez que les articles de démonstration payés contribuent au revenu du tableau de bord.",
  "Confirm that pending or unpaid demo items do not count as paid revenue.":
    "Confirmez que les articles de démonstration en attente ou non payés ne comptent pas comme revenu payé.",
  "Treat payout readiness as pending until live Stripe setup is approved outside staging.":
    "Considérez la préparation des versements comme en attente jusqu’à l’approbation de la configuration Stripe réelle hors staging.",
  "Report any disagreement between payments, revenue cards, graph, and sold items.":
    "Signalez tout désaccord entre paiements, cartes de revenu, graphique et articles vendus.",
  "No real payment capture": "Aucune capture de paiement réelle",
  "Staging payment rows exist to exercise reporting and fulfilment handoff. They are not live settlement, billing, or payout approval.":
    "Les lignes de paiement staging servent à tester le reporting et le relais d’exécution. Elles ne constituent pas un règlement, une facturation ou une approbation de versement réelle.",
  "Open Messages or Fola™ from the pharmacy header.":
    "Ouvrez Messages ou Fola™ depuis l’en-tête pharmacie.",
  "Use the prompt menu for pharmacy operations questions.":
    "Utilisez le menu de prompts pour les questions d’opérations pharmacie.",
  "Ask about branch roles, staff invite copy, fulfilment handoff, stock setup, or settlement concepts.":
    "Posez des questions sur les rôles d’officine, le texte d’invitation du personnel, le relais d’exécution, la configuration du stock ou les notions de règlement.",
  "Do not use Fola™ for diagnosis, medication substitution advice, prescription routing, or admin/KPI data.":
    "N’utilisez pas Fola™ pour le diagnostic, les conseils de substitution de médicaments, le routage des prescriptions ou les données admin/KPI.",
  "Report any broken prompt, admin leakage, or misleading capability claim.":
    "Signalez tout prompt cassé, fuite admin ou affirmation de capacité trompeuse.",
  "Bounded staging assistant": "Assistant staging borné",
  "Pharmacy Fola™ provides operational staging guidance. It does not replace clinical judgment, live inventory systems, or AfriMed™ admin controls.":
    "Fola™ pharmacie fournit une aide opérationnelle staging. Il ne remplace pas le jugement clinique, les systèmes d’inventaire réels ou les contrôles admin AfriMed™.",
  "Open Settings from the pharmacy header.":
    "Ouvrez Réglages depuis l’en-tête pharmacie.",
  "Review role explanations before inviting a staff member.":
    "Vérifiez les explications de rôles avant d’inviter un membre du personnel.",
  "Invite members using their correct work email address.":
    "Invitez les membres avec leur adresse email professionnelle correcte.",
  "Assign the smallest role that lets the person test their branch task.":
    "Attribuez le rôle le plus limité permettant à la personne de tester sa tâche d’officine.",
  "Remember that staff roles should not change ownership, remove the pharmacy, or manage payout settings unless explicitly allowed.":
    "Rappelez-vous que les rôles du personnel ne doivent pas changer la propriété, supprimer l’officine ou gérer les réglages de versement sauf autorisation explicite.",
  "Owner remains the permission anchor": "Le propriétaire reste l’ancrage des permissions",
  "The branch owner or test owner manages profile, staff invitations, role assignments, fulfilment oversight, and pharmacy settings during staging.":
    "Le propriétaire de l’officine ou du test gère le profil, les invitations du personnel, l’attribution des rôles, la supervision de l’exécution et les réglages pharmacie pendant staging.",
  "Check the notification bell after launching the pharmacy dashboard.":
    "Vérifiez la cloche de notification après lancement du tableau de bord pharmacie.",
  "Confirm that sample prescription requests appear in the fulfilment queue.":
    "Confirmez que les demandes de prescription exemples apparaissent dans la file d’exécution.",
  "Compare notification count, prescription rows, dashboard cards, and inventory alerts.":
    "Comparez le compteur de notifications, les lignes de prescription, les cartes du tableau de bord et les alertes d’inventaire.",
  "Refresh or navigate between pharmacy tabs to confirm seeded data remains stable.":
    "Actualisez ou naviguez entre les onglets pharmacie pour confirmer que les données préchargées restent stables.",
  "Report any mismatch between notification count and actionable prescription rows.":
    "Signalez tout écart entre le compteur de notifications et les lignes de prescription actionnables.",
  "Counts must stay trustworthy": "Les compteurs doivent rester fiables",
  "If notification counts, queue rows, or dashboard metrics disagree, treat it as a staging reporting integrity issue.":
    "Si les compteurs de notification, les lignes de file ou les métriques du tableau de bord divergent, traitez cela comme un problème d’intégrité du reporting staging.",
  "Dashboard and inventory": "Tableau de bord et inventaire",
  "Inventory and dashboard cards should hydrate from the same pharmacy branch scope.":
    "Les cartes d’inventaire et de tableau de bord doivent se remplir depuis le même périmètre d’officine.",
  "Fulfilment playbook": "Guide d’exécution",
  "Use the playbook to keep fulfilment actions ordered and safe.":
    "Utilisez le guide pour garder les actions d’exécution ordonnées et sûres.",
  "Prescription fulfilment request": "Demande d’exécution de prescription",
  "Open a seeded request and move it through the branch workflow.":
    "Ouvrez une demande préchargée et faites-la avancer dans le parcours de l’officine.",
  "Prescription detail": "Détail de prescription",
  "Review prescription context before confirming availability or fulfilment actions.":
    "Vérifiez le contexte de prescription avant de confirmer la disponibilité ou les actions d’exécution.",
  "Payments and Messages": "Paiements et messages",
  "Payments and Messages should stay in pharmacy context and never route into admin dashboards.":
    "Paiements et Messages doivent rester dans le contexte pharmacie et ne jamais être routés vers les tableaux de bord admin.",
  "Messages and Fola™ entry": "Entrée Messages et Fola™",
  "Messages and Fola™ remain inside pharmacy-safe routes.":
    "Messages et Fola™ restent dans des routes sûres pour la pharmacie.",
  "Pharmacy account and permissions": "Compte pharmacie et permissions",
  "Review owner/member identity and privilege handoff before inviting staff.":
    "Vérifiez l’identité propriétaire/membre et le relais de privilèges avant d’inviter du personnel.",
  "Dashboard notifications and metrics": "Notifications et métriques du tableau de bord",
  "Dashboard metrics and inventory alerts should remain stable across refresh and navigation.":
    "Les métriques du tableau de bord et les alertes d’inventaire doivent rester stables lors de l’actualisation et de la navigation.",
  "Subspecialty and registration upload": "Sous-spécialité et téléversement d’inscription",
  "Add subspecialty details and upload registration evidence for later verification review.":
    "Ajoutez les détails de sous-spécialité et téléversez une preuve d’inscription pour revue ultérieure.",
  "Formation and registration details": "Formation et détails d’inscription",
  "Enter training and registration information carefully so later review has the right source details.":
    "Saisissez soigneusement les informations de formation et d’inscription afin que la revue ultérieure dispose des bonnes sources.",
  "About section and Fola draft": "Section À propos et brouillon Fola",
  "Draft a clear clinician bio, review the wording, and keep patient-facing claims accurate.":
    "Rédigez une bio clinicien claire, relisez le texte et gardez les affirmations visibles par les patients exactes.",
  "Finish onboarding, then move into the Settings welcome screen for the clinician account.":
    "Terminez l’onboarding, puis passez à l’écran d’accueil des réglages du compte clinicien.",
  "Doctor Settings home": "Accueil des réglages médecin",
  "Review the doctor Settings home so profile, verification, and practice details can be checked.":
    "Vérifiez l’accueil des réglages médecin afin de contrôler le profil, la vérification et les détails de pratique.",
  "Thread summary screenshot slot": "Emplacement de capture du résumé de conversation",
  "Add the thread summary screen when Fola summary captures are approved.":
    "Ajoutez l’écran de résumé de conversation lorsque les captures de résumé Fola seront approuvées.",
  "Draft reply screenshot slot": "Emplacement de capture de brouillon de réponse",
  "Add a draft reply example once safe messaging screenshots are captured.":
    "Ajoutez un exemple de brouillon de réponse lorsque les captures de messages sûrs seront disponibles.",
  "Medication explanation screenshot slot": "Emplacement de capture d’explication de médicament",
  "Add a medication explanation screen only after clinical safety review.":
    "Ajoutez un écran d’explication de médicament uniquement après revue de sécurité clinique.",
  "Open your profile settings.": "Ouvrez les réglages de votre profil.",
  "Review contact fields such as email, phone, location, and address.":
    "Vérifiez les champs de contact comme email, téléphone, localisation et adresse.",
  "Update details that are out of date.": "Mettez à jour les informations obsolètes.",
  "Save changes before leaving the screen.": "Enregistrez les changements avant de quitter l’écran.",
  "Communication accuracy": "Exactitude des communications",
  "Accurate contact details help AfriMed™ send appointment and account information to the right place.":
    "Des coordonnées exactes aident AfriMed™ à envoyer les informations de rendez-vous et de compte au bon endroit.",
  "Contact details screenshot slot": "Emplacement de capture des coordonnées",
  "Add the profile contact screen after the settings walkthrough captures are approved.":
    "Ajoutez l’écran de contact du profil après approbation des captures du parcours des réglages.",
  "Open your profile or dependant area.": "Ouvrez votre profil ou l’espace personnes à charge.",
  "Add the dependant details requested by AfriMed™.":
    "Ajoutez les informations de la personne à charge demandées par AfriMed™.",
  "Use the dependant selector during booking when the appointment is for that person.":
    "Utilisez le sélecteur de personne à charge pendant la réservation lorsque le rendez-vous concerne cette personne.",
  "Review dependant information before confirming care-related actions.":
    "Vérifiez les informations de la personne à charge avant de confirmer des actions liées aux soins.",
  "Dependant care is guardian-managed during staging. Separate dependant login and privacy handoff flows are future updates.":
    "Les soins des personnes à charge sont gérés par un responsable pendant staging. La connexion séparée et les relais de confidentialité sont prévus pour de futures mises à jour.",
  "Dependants screenshot slot": "Emplacement de capture des personnes à charge",
  "Add the dependant management screen once the guardian walkthrough is captured.":
    "Ajoutez l’écran de gestion des personnes à charge lorsque le parcours responsable sera capturé.",
  "Open the insurance area in your profile or booking flow.":
    "Ouvrez l’espace assurance dans votre profil ou votre parcours de réservation.",
  "Select or enter the insurance information currently supported.":
    "Sélectionnez ou saisissez les informations d’assurance actuellement prises en charge.",
  "Review how the payment method is shown before confirming an appointment.":
    "Vérifiez l’affichage du mode de paiement avant de confirmer un rendez-vous.",
  "Update details when your cover or preferred payment method changes.":
    "Mettez à jour les informations lorsque votre couverture ou mode de paiement préféré change.",
  "Insurance selection helps record how you intend to pay. Live insurance claims automation is not enabled yet.":
    "La sélection de l’assurance aide à enregistrer comment vous prévoyez de payer. L’automatisation réelle des demandes de remboursement n’est pas encore activée.",
  "Insurance screenshot slot": "Emplacement de capture de l’assurance",
  "Add the insurance details screen when payment and cover walkthroughs are captured.":
    "Ajoutez l’écran des détails d’assurance lorsque les parcours paiement et couverture seront capturés.",
  "Open your profile or myEHR area.": "Ouvrez votre profil ou l’espace mon DME.",
  "Review the available sections for conditions, allergies, visits, prescriptions, notes, and tests.":
    "Vérifiez les sections disponibles pour conditions, allergies, visites, prescriptions, notes et tests.",
  "Update fields that AfriMed™ allows you to maintain directly.":
    "Mettez à jour les champs qu’AfriMed™ vous permet de gérer directement.",
  "Ask your clinician to review medical details that need clinical confirmation.":
    "Demandez à votre clinicien de revoir les informations médicales nécessitant une confirmation clinique.",
  "Clinical review matters": "La revue clinique compte",
  "Medical history helps care teams understand context, but clinical decisions remain clinician-reviewed.":
    "Les antécédents médicaux aident les équipes de soins à comprendre le contexte, mais les décisions cliniques restent revues par un clinicien.",
  "myEHR screenshot slot": "Emplacement de capture mon DME",
  "Add a readable health record screen once myEHR walkthrough images are ready.":
    "Ajoutez un écran de dossier de santé lisible lorsque les images du parcours mon DME seront prêtes.",
  "Use your own authenticated AfriMed™ account for private healthcare actions.":
    "Utilisez votre propre compte AfriMed™ authentifié pour les actions de santé privées.",
  "Check account and security settings regularly.":
    "Vérifiez régulièrement les réglages du compte et de sécurité.",
  "Avoid sharing screenshots that include personal health details.":
    "Évitez de partager des captures contenant des informations personnelles de santé.",
  "Use support channels when something looks incorrect or exposed in the wrong place.":
    "Utilisez les canaux d’assistance lorsqu’un élément semble incorrect ou exposé au mauvais endroit.",
  "Private by context": "Privé selon le contexte",
  "Only authenticated users with the right care relationship should access private health information inside AfriMed™.":
    "Seuls les utilisateurs authentifiés disposant de la bonne relation de soins doivent accéder aux informations de santé privées dans AfriMed™.",
  "Privacy settings screenshot slot": "Emplacement de capture des réglages de confidentialité",
  "Add the account and privacy settings screen when security walkthrough captures are available.":
    "Ajoutez l’écran de compte et de confidentialité lorsque les captures du parcours sécurité seront disponibles.",
  "Open Prescriptions or myEHR from the area available to your account.":
    "Ouvrez Prescriptions ou mon DME depuis l’espace disponible pour votre compte.",
  "Select the prescription record you want to review.":
    "Sélectionnez le dossier de prescription que vous souhaitez consulter.",
  "Check medicine name, dose, timing, clinician, date, and status where shown.":
    "Vérifiez le nom du médicament, la dose, l’horaire, le clinicien, la date et le statut lorsqu’ils sont affichés.",
  "Ask your clinician or pharmacist before changing how you take medicine.":
    "Demandez conseil à votre clinicien ou pharmacien avant de modifier la prise d’un médicament.",
  "Medication safety": "Sécurité des médicaments",
  "Prescription records support understanding. Treatment changes must be clinician or pharmacist reviewed.":
    "Les dossiers de prescription aident à comprendre. Toute modification de traitement doit être revue par un clinicien ou un pharmacien.",
  "Prescription screenshot slot": "Emplacement de capture de prescription",
  "Add the prescription record screen after clinical safety review.":
    "Ajoutez l’écran de dossier de prescription après revue de sécurité clinique.",
  "Open myEHR from your profile, records, or prescriptions area.":
    "Ouvrez mon DME depuis votre profil, vos dossiers ou l’espace prescriptions.",
  "Review sections such as visits, notes, prescriptions, allergies, and medical history where available.":
    "Vérifiez les sections comme visites, notes, prescriptions, allergies et antécédents lorsqu’elles sont disponibles.",
  "Use the information to prepare for appointments or understand previous care.":
    "Utilisez ces informations pour préparer les rendez-vous ou comprendre les soins précédents.",
  "Ask a clinician to review anything that appears incorrect or clinically important.":
    "Demandez à un clinicien de revoir tout élément qui paraît incorrect ou cliniquement important.",
  "Readable health context": "Contexte de santé lisible",
  "myEHR helps organize information, but AfriMed™ does not turn records into self-diagnosis or automatic treatment advice.":
    "Mon DME aide à organiser l’information, mais AfriMed™ ne transforme pas les dossiers en autodiagnostic ou conseils de traitement automatiques.",
  "myEHR overview screenshot slot": "Emplacement de capture d’aperçu mon DME",
  "Add the myEHR overview screen once health record captures are ready.":
    "Ajoutez l’écran d’aperçu mon DME lorsque les captures de dossier de santé seront prêtes.",
  "Open the prescription, invoice, note, or record you want to keep.":
    "Ouvrez la prescription, facture, note ou dossier que vous souhaitez conserver.",
  "Use the download action if it is visible and enabled for your account.":
    "Utilisez l’action de téléchargement si elle est visible et activée pour votre compte.",
  "Store the downloaded file somewhere private and secure.":
    "Conservez le fichier téléchargé dans un endroit privé et sécurisé.",
  "Share records only with people or clinicians you trust.":
    "Ne partagez les dossiers qu’avec des personnes ou cliniciens de confiance.",
  "Some record downloads are still being expanded. If no download action appears, the record may be view-only during staging.":
    "Certains téléchargements de dossiers sont encore en développement. Si aucune action de téléchargement n’apparaît, le dossier peut être en lecture seule pendant staging.",
  "Record download screenshot slot": "Emplacement de capture de téléchargement de dossier",
  "Add the download action screen when export walkthroughs are captured.":
    "Ajoutez l’écran de téléchargement lorsque les parcours d’export seront capturés.",
  "Open the appointment, service, or payment step.":
    "Ouvrez le rendez-vous, le service ou l’étape de paiement.",
  "At Secure Checkout, review the amount, currency, patient, clinician, service, and appointment time.":
    "Dans le paiement sécurisé, vérifiez le montant, la devise, le patient, le clinicien, le service et l’heure du rendez-vous.",
  "Choose an available method: Credit • Debit Card, Instant EFT, Scan to Pay, MTN MoMo, or Sponsor payment.":
    "Choisissez un mode disponible : Carte de crédit • débit, Instant EFT, Scan to Pay, MTN MoMo ou Paiement par sponsor.",
  "Complete the secure provider step shown for the selected method, then return to AfriMed™ if prompted.":
    "Effectuez l’étape sécurisée du prestataire affichée pour le mode choisi, puis revenez dans AfriMed™ si cela vous est demandé.",
  "Wait for AfriMed™ to verify the payment. A booking is not created before verified payment, which prevents an unpaid or duplicate appointment.":
    "Attendez qu’AfriMed™ vérifie le paiement. Aucune réservation n’est créée avant cette vérification, ce qui évite un rendez-vous impayé ou en double.",
  "After verification, AfriMed™ creates the booking and shows the normal booking confirmation.":
    "Après vérification, AfriMed™ crée la réservation et affiche la confirmation normale du rendez-vous.",
  "Verified payment protects the booking": "Le paiement vérifié protège la réservation",
  "Available methods can differ by market. AfriMed™ shows the supported options for the booking and creates the appointment only after it receives verified payment confirmation.":
    "Les modes disponibles peuvent varier selon le marché. AfriMed™ affiche les options prises en charge pour la réservation et ne crée le rendez-vous qu’après réception d’une confirmation de paiement vérifiée.",
  "Review the available payment methods for your account and market.":
    "Vérifiez les modes de paiement disponibles pour votre compte et votre marché.",
  "Choose the method that matches how you intend to pay.":
    "Choisissez le mode correspondant à la façon dont vous prévoyez de payer.",
  "Confirm only after checking the amount, currency, and person receiving care.":
    "Confirmez uniquement après avoir vérifié le montant, la devise et la personne qui reçoit les soins.",
  "Payment options can differ by market, staging account, and enabled workflows. AfriMed™ should show only the options currently available to you.":
    "Les options de paiement peuvent varier selon le marché, le compte staging et les parcours activés. AfriMed™ doit afficher uniquement les options actuellement disponibles pour vous.",
  "Payment method screenshot slot": "Emplacement de capture du mode de paiement",
  "Add the payment method screen once billing walkthrough captures are available.":
    "Ajoutez l’écran du mode de paiement lorsque les captures du parcours facturation seront disponibles.",
  "Open Payments, invoices, or the relevant appointment detail.":
    "Ouvrez Paiements, factures ou le détail du rendez-vous concerné.",
  "Open Transactions & invoices from Settings and select the relevant payment or refund event.":
    "Ouvrez Transactions et factures depuis les Réglages, puis sélectionnez le paiement ou le remboursement concerné.",
  "Use Appointment receipt for the original verified payment and Refund receipt for the reversal outcome.":
    "Utilisez le Reçu de rendez-vous pour le paiement initial vérifié et le Reçu de remboursement pour le résultat de l’annulation de paiement.",
  "Check the patient, clinician, appointment, consultation or location, payment method, amount, status, and reference.":
    "Vérifiez le patient, le clinicien, le rendez-vous, la consultation ou le lieu, le mode de paiement, le montant, le statut et la référence.",
  "Use Print to open the print-ready receipt, or Download receipt to save the PDF.":
    "Utilisez Imprimer pour ouvrir le reçu prêt à imprimer, ou Télécharger le reçu pour enregistrer le PDF.",
  "A refunded payment remains visible alongside the original Paid event so the financial history stays complete.":
    "Un paiement remboursé reste visible avec l’événement Payé initial afin que l’historique financier demeure complet.",
  "Refund destination": "Destination du remboursement",
  "Refunded to the original payment method. Processing time after AfriMed™ confirms the refund can depend on the original payment method and financial institution.":
    "Remboursé sur le mode de paiement initial. Le délai après confirmation du remboursement par AfriMed™ peut dépendre du mode de paiement initial et de l’établissement financier.",
  "Select the receipt or invoice you want to review.":
    "Sélectionnez le reçu ou la facture que vous souhaitez consulter.",
  "Check date, amount, currency, status, and appointment reference.":
    "Vérifiez la date, le montant, la devise, le statut et la référence du rendez-vous.",
  "Download the receipt if the export action is available.":
    "Téléchargez le reçu si l’action d’export est disponible.",
  "Staging note": "Note staging",
  "Receipts and exports are still being expanded. Some records may show as pending or view-only until payment tooling is fully enabled.":
    "Les reçus et exports sont encore en développement. Certains dossiers peuvent apparaître en attente ou en lecture seule jusqu’à l’activation complète des outils de paiement.",
  "Invoice screenshot slot": "Emplacement de capture de facture",
  "Add the invoice or receipt screen when billing captures are ready.":
    "Ajoutez l’écran de facture ou reçu lorsque les captures de facturation seront prêtes.",
  "If payment is pending, keep the selected booking details and use Check payment status or pending-payment recovery instead of starting another payment.":
    "Si le paiement est en attente, conservez les détails de réservation sélectionnés et utilisez Vérifier le statut du paiement ou la récupération du paiement en attente au lieu de recommencer.",
  "A failed, declined, rejected, or expired payment does not create a booking; choose an available method and try again when ready.":
    "Un paiement échoué, refusé, rejeté ou expiré ne crée pas de réservation ; choisissez un mode disponible et réessayez lorsque vous êtes prêt.",
  "If verified payment completed but confirmation was interrupted, AfriMed™ recovers the payment status before creating the booking, avoiding a duplicate booking request.":
    "Si le paiement vérifié a abouti mais que la confirmation a été interrompue, AfriMed™ récupère le statut avant de créer la réservation, afin d’éviter une demande en double.",
  "When a paid appointment is cancelled, AfriMed™ records the cancellation and starts or records the required payment reversal.":
    "Lorsqu’un rendez-vous payé est annulé, AfriMed™ enregistre l’annulation et lance ou consigne l’annulation de paiement requise.",
  "When the reversal is verified, the refund is returned to the original payment method and appears in Transactions & invoices with a Refund receipt.":
    "Lorsque l’annulation de paiement est vérifiée, le statut « Remboursé sur le mode de paiement initial » apparaît dans Transactions et factures avec un Reçu de remboursement.",
  "Use Notifications and Transactions & invoices to follow payment confirmation, reversal, and refund status.":
    "Utilisez Notifications et Transactions et factures pour suivre la confirmation, l’annulation et le remboursement du paiement.",
  "Do not pay twice": "Ne payez pas deux fois",
  "If a payment appears pending or the confirmation page was interrupted, recover or check that payment first. Start another payment only after AfriMed™ confirms that the earlier attempt failed or expired.":
    "Si un paiement apparaît en attente ou si la page de confirmation a été interrompue, récupérez ou vérifiez d’abord ce paiement. N’en démarrez un autre qu’après qu’AfriMed™ confirme l’échec ou l’expiration de la tentative précédente.",
  "Select the insurance option currently supported for your account.":
    "Sélectionnez l’option d’assurance actuellement prise en charge pour votre compte.",
  "Review the selected cover before confirming an appointment.":
    "Vérifiez la couverture sélectionnée avant de confirmer un rendez-vous.",
  "Use support if the insurer shown does not match your records.":
    "Utilisez l’assistance si l’assureur affiché ne correspond pas à vos dossiers.",
  "Insurance selection is for visibility during staging. Automated claims submission and approval are still expanding.":
    "La sélection de l’assurance sert à la visibilité pendant staging. La soumission et l’approbation automatisées des demandes de remboursement sont encore en développement.",
  "Insurance selection screenshot slot": "Emplacement de capture de sélection d’assurance",
  "Add the insurance selection screen once cover workflows are captured.":
    "Ajoutez l’écran de sélection d’assurance lorsque les parcours de couverture seront capturés.",
  "Open the payment or sponsor payment area when available.":
    "Ouvrez l’espace paiement ou paiement par sponsor lorsqu’il est disponible.",
  "Choose Sponsor payment at Secure Checkout or prepare a sponsor payment from Transactions & invoices.":
    "Choisissez Paiement par sponsor dans le paiement sécurisé ou préparez-le depuis Transactions et factures.",
  "Check the patient, amount, currency, appointment, and reason before sharing the secure AfriMed™ payment link.":
    "Vérifiez le patient, le montant, la devise, le rendez-vous et le motif avant de partager le lien de paiement AfriMed™ sécurisé.",
  "The sponsor completes the enabled payment route without receiving access to the Patient's clinical record.":
    "Le sponsor effectue le paiement activé sans recevoir d’accès au dossier clinique du Patient.",
  "Return to AfriMed™ and confirm the verified payment status; the booking is created only after that verification.":
    "Revenez dans AfriMed™ et confirmez le statut vérifié ; la réservation n’est créée qu’après cette vérification.",
  "If the sponsor return is interrupted, use pending-payment recovery instead of creating a second payment.":
    "Si le retour du sponsor est interrompu, utilisez la récupération du paiement en attente au lieu de créer un second paiement.",
  "Sponsor privacy": "Confidentialité du sponsor",
  "The sponsor pays through an AfriMed™ payment link. Payment support does not grant access to appointments, messages, health records, or other clinical information.":
    "Le sponsor paie via un lien AfriMed™. L’aide au paiement ne donne accès ni aux rendez-vous, ni aux messages, ni aux dossiers médicaux, ni à d’autres informations cliniques.",
  "Prepare the patient, amount, currency, and reason for payment.":
    "Préparez le patient, le montant, la devise et la raison du paiement.",
  "Generate or share a secure payment link only when the action is enabled.":
    "Générez ou partagez un lien de paiement sécurisé uniquement lorsque l’action est activée.",
  "Confirm payment status in AfriMed™ before treating the payment as complete.":
    "Confirmez le statut du paiement dans AfriMed™ avant de considérer le paiement comme terminé.",
  "Sponsor workflows are being prepared for family and diaspora payments. Do not assume a sponsor link is live unless AfriMed™ shows it as enabled.":
    "Les parcours sponsor sont préparés pour les paiements familiaux et diaspora. Ne supposez pas qu’un lien sponsor est actif sauf si AfriMed™ l’affiche comme activé.",
  "Sponsor payment screenshot slot": "Emplacement de capture du paiement sponsor",
  "Add the sponsor payment screen when link generation captures are approved.":
    "Ajoutez l’écran de paiement sponsor lorsque les captures de génération de lien seront approuvées.",
  "Check that your internet connection is working.":
    "Vérifiez que votre connexion internet fonctionne.",
  "Refresh the page or reopen AfriMed™.":
    "Actualisez la page ou rouvrez AfriMed™.",
  "Confirm you are using the correct email address for your account.":
    "Confirmez que vous utilisez la bonne adresse email pour votre compte.",
  "Use Help & support if access still does not work after trying the basics.":
    "Utilisez Aide et assistance si l’accès ne fonctionne toujours pas après les vérifications de base.",
  "Support access": "Accès à l’assistance",
  "Use Help & support to create a request and review its status. If access cannot be confirmed, retry or use the account-access guidance.":
    "Utilisez Aide et assistance pour créer une demande et consulter son statut. Si l’accès ne peut pas être confirmé, réessayez ou consultez l’aide à la connexion.",
  "Access support screenshot slot": "Emplacement de capture de l’assistance accès",
  "Add sign-in or access error screens once troubleshooting captures are ready.":
    "Ajoutez les écrans de connexion ou d’erreur d’accès lorsque les captures de dépannage seront prêtes.",
  "Use a current browser version on a device with enough battery and network signal.":
    "Utilisez une version récente du navigateur sur un appareil avec suffisamment de batterie et de réseau.",
  "Allow permissions for camera, microphone, files, or notifications only when the AfriMed™ screen asks for them.":
    "Autorisez caméra, micro, fichiers ou notifications uniquement lorsque l’écran AfriMed™ les demande.",
  "Try refreshing the page if a screen looks incomplete.":
    "Essayez d’actualiser la page si un écran paraît incomplet.",
  "Send support the screen name and what you expected to happen if the issue continues.":
    "Envoyez à l’assistance le nom de l’écran et ce que vous attendiez si le problème continue.",
  "Share safely": "Partager en sécurité",
  "When reporting technical issues, avoid sending screenshots that expose private health details unless support specifically asks for them through an approved channel.":
    "Lorsque vous signalez un problème technique, évitez d’envoyer des captures exposant des informations de santé privées sauf si l’assistance les demande par un canal approuvé.",
  "Technical support screenshot slot": "Emplacement de capture d’assistance technique",
  "Add browser permission or device troubleshooting screens once captured.":
    "Ajoutez les écrans de permissions navigateur ou de dépannage appareil lorsqu’ils seront capturés.",
  "Record the email address used for the pharmacy staging account.":
    "Notez l’adresse email utilisée pour le compte pharmacie staging.",
  "Copy the current route where the issue appears.":
    "Copiez la route actuelle où le problème apparaît.",
  "Note whether the issue affects onboarding, dashboard, inventory, payments, messages, Fola™, notifications, or fulfilment.":
    "Indiquez si le problème touche l’onboarding, le tableau de bord, l’inventaire, les paiements, les messages, Fola™, les notifications ou l’exécution.",
  "Include a screenshot when no real patient or private information is exposed.":
    "Incluez une capture uniquement lorsqu’aucune information réelle de patient ou donnée privée n’est exposée.",
  "Send the report through pharmacy Messages/Fola™ or the founder-approved feedback path.":
    "Envoyez le signalement via Messages/Fola™ pharmacie ou le canal de retour approuvé par le fondateur.",
  "Evidence helps fix trust issues faster": "Les preuves aident à corriger plus vite les problèmes de confiance",
  "Route, account, screenshot, and timing details help distinguish real data issues from stale browser state or local cache.":
    "La route, le compte, la capture et l’heure aident à distinguer les vrais problèmes de données d’un état navigateur obsolète ou d’un cache local.",
};

function translateHelpText(value) {
  if (typeof value !== "string") return value;
  return HELP_FRENCH_TEXT_COPY[value] || value;
}

function isFrenchLanguage(languageCode = "") {
  return String(languageCode || "").toLowerCase().startsWith("fr");
}

function localizeHelpCategory(category, isFrench) {
  if (!isFrench || !category) return category;
  if (category.audience === 'pharmacy' && category.fr) return {
    ...category, ...category.fr,
    articles: category.articles.map(article => localizeHelpArticle({...article, audience: 'pharmacy', categorySlug: category.slug, categoryTitle: category.fr.title}, true)),
  };
  const copy =
    HELP_FRENCH_CATEGORY_COPY[`${category.audience || "patient"}:${category.slug}`] ||
    HELP_FRENCH_CATEGORY_COPY[category.slug];
  if (!copy) return category;
  return {
    ...category,
    title: copy.title || category.title,
    summary: copy.summary || category.summary,
    status:
      category.status === "Ready"
        ? "Prêt"
        : category.status === "Currently expanding"
          ? "En développement"
          : category.status,
    articles: Array.isArray(category.articles)
      ? category.articles.map((article) =>
          localizeHelpArticle(
            {
              ...article,
              categoryTitle: copy.title || article.categoryTitle,
            },
            isFrench,
          ),
        )
      : category.articles,
  };
}

function localizeHelpArticle(article, isFrench) {
  if (!isFrench || !article) return article;
  if (article.fr) return { ...article, ...article.fr,
    categoryTitle: resolveHelpCategory(article.categorySlug, 'pharmacy')?.fr?.title || article.categoryTitle,
  };
  const categoryCopy =
    HELP_FRENCH_CATEGORY_COPY[`${article.audience || "patient"}:${article.categorySlug}`] ||
    HELP_FRENCH_CATEGORY_COPY[article.categorySlug];
  const articleCopy = HELP_FRENCH_ARTICLE_COPY[article.slug];
  const localizeScreenshot = (screenshot = {}) => ({
    ...screenshot,
    label: articleCopy?.screenshot?.label || translateHelpText(screenshot.label),
    caption: articleCopy?.screenshot?.caption || translateHelpText(screenshot.caption),
    alt: articleCopy?.screenshot?.alt || translateHelpText(screenshot.alt),
  });
  const localizeCallout = (callout) => {
    if (!callout && !articleCopy?.callout) return callout;
    const merged = {
      ...(callout || {}),
      ...(articleCopy?.callout || {}),
    };
    return {
      ...merged,
      title: translateHelpText(merged.title),
      body: translateHelpText(merged.body),
    };
  };
  return {
    ...article,
    title: articleCopy?.title || article.title,
    summary: articleCopy?.summary || article.summary,
    steps: articleCopy?.steps || (Array.isArray(article.steps) ? article.steps.map(translateHelpText) : article.steps),
    questions: articleCopy?.questions || article.questions,
    callout: localizeCallout(article.callout),
    screenshot: article.screenshot ? localizeScreenshot(article.screenshot) : article.screenshot,
    screenshots: Array.isArray(article.screenshots)
      ? article.screenshots.map(localizeScreenshot)
      : article.screenshots,
    categoryTitle: categoryCopy?.title || article.categoryTitle,
  };
}

function normalizeHelpAudience(value) {
  const normalized = String(value || "").toLowerCase().trim();
  if (normalized === "doctor" || normalized === "pharmacy") return normalized;
  return "patient";
}

function getDefaultHelpAudience(profile) {
  return normalizeHelpAudience(profile?.role);
}

function getHelpAudienceStorageKey(userId) {
  const owner = String(userId || "").trim();
  return owner ? `afrimed:help-audience:${owner}` : "";
}

function useHelpAudience(audienceOverride = "") {
  const { user, profile } = useAuth() || {};
  const forcedAudience = normalizeHelpAudience(audienceOverride);
  const hasForcedAudience = ["patient", "doctor", "pharmacy"].includes(
    String(audienceOverride || "").toLowerCase().trim()
  );
  const location = useLocation();
  const requestedAudience = requestedHelpAudience(location.search);
  const defaultAudience = requestedAudience || getDefaultHelpAudience(profile);
  const storageKey = getHelpAudienceStorageKey(user?.id);
  const [audience, setAudienceState] = React.useState(
    hasForcedAudience ? forcedAudience : defaultAudience
  );

  React.useEffect(() => {
    if (hasForcedAudience) {
      setAudienceState(forcedAudience);
      return;
    }

    if (!storageKey) {
      setAudienceState(defaultAudience);
      return;
    }

    try {
      const saved = localStorage.getItem(storageKey);
      setAudienceState(requestedAudience || (saved ? normalizeHelpAudience(saved) : defaultAudience));
    } catch {
      setAudienceState(defaultAudience);
    }
  }, [defaultAudience, forcedAudience, hasForcedAudience, storageKey, requestedAudience]);

  const setAudience = React.useCallback(
    (next) => {
      if (hasForcedAudience) return;
      const normalized = normalizeHelpAudience(next);
      setAudienceState(normalized);
      if (!storageKey) return;
      try {
        localStorage.setItem(storageKey, normalized);
      } catch {
        // Keep the in-memory selection if browser storage is unavailable.
      }
    },
    [hasForcedAudience, storageKey]
  );

  return [hasForcedAudience ? forcedAudience : requestedAudience || audience, setAudience];
}

function HelpAudienceSwitch({ audience, onChange, isFrench = false }) {
  return (
    <div className="flex w-full min-w-0 justify-center">
      <AfriSwitch
        value={audience}
        onChange={onChange}
        options={isFrench ? HELP_FRENCH_AUDIENCE_OPTIONS : HELP_AUDIENCE_OPTIONS}
        ariaLabel={isFrench ? "Choisir le rôle du centre d’aide" : "Choose Help Centre role"}
        className="w-full max-w-[360px] justify-between bg-[rgb(var(--clr-surface-cont-highest))] [&_button]:min-w-0 [&_button]:flex-1"
      />
    </div>
  );
}

function filterArticles(query, audience) {
  const needle = query.trim().toLowerCase();
  const articles = getHelpArticlesForAudience(audience);
  if (!needle) return articles.slice(0, 6);

  return articles.filter((article) =>
    [article.title, article.summary, article.categoryTitle, ...(article.steps || [])]
      .join(" ")
      .toLowerCase()
      .includes(needle)
  );
}

function getHelpRoleCopy(audience) {
  if (audience === "doctor") {
    return {
      roleLabel: "doctor",
      intro:
        "Step-by-step doctor guidance for accounts, clinical profiles, appointments, messages, prescriptions, privacy, and support. Built to become screenshot-led as the product walkthrough library grows.",
      section:
        "Start with the doctor area you are trying to use. Sections marked as expanding are ready for screenshots and future articles.",
      foundation:
        "The doctor foundation focuses on profile confidence, appointment workflow, and role-safe clinical guidance.",
    };
  }
  if (audience === "pharmacy") {
    return {
      roleLabel: "pharmacy",
      intro:
        "Step-by-step pharmacy guidance for onboarding, branch setup, inventory, prescription fulfilment, payments, Fola™, team permissions, notifications, and support.",
      section:
        "Start with the pharmacy area you are trying to use. Each guide separates branch instructions from Staging demonstrations.",
      foundation:
        "The pharmacy foundation focuses on dashboard confidence, seeded fulfilment workflows, inventory visibility, payments, and role-safe branch operations.",
    };
  }
  return {
    roleLabel: "patient",
    intro:
      "Step-by-step patient guidance for accounts, profiles, appointments, messages, Fola, prescriptions, privacy, and support. Built to become screenshot-led as the product walkthrough library grows.",
    section:
      "Start with the patient area you are trying to use. Sections marked as expanding are ready for screenshots and future articles.",
    foundation:
      "The patient foundation focuses on account setup, profile confidence, and safe Fola guidance.",
  };
}

function PharmacyGuideNotice({ isFrench }) {
  return <SettingsCard fullWidth className="min-w-0 px-5 py-4">
    <p className="text-body-medium text-on-surface-var">{isFrench
      ? 'Ces guides décrivent les parcours existants. Les services utilisables dépendent de votre compte, de votre officine et de l’environnement publié. Les captures conservées montrent la démonstration en anglais ; les légendes et étapes sont traduites. L’accès au guide ne vaut pas approbation opérationnelle.'
      : 'These guides describe existing workflows. Available services depend on your account, branch and released environment. Retained screenshots show the demonstration; guide access does not establish operational approval.'}</p>
  </SettingsCard>;
}

function HelpLanding({ audienceOverride = "", basePath = "/help", pharmacyShell = false }) {
  const navigate = useNavigate();
  const { languageCode } = useHelpLanguage();
  const isFrench = isFrenchLanguage(languageCode);
  const [audience, setAudience] = useHelpAudience(audienceOverride);
  const hasAudienceOverride = Boolean(String(audienceOverride || "").trim());
  const [query, setQuery] = React.useState("");
  const categories = React.useMemo(
    () =>
      getHelpCategoriesForAudience(audience).map((category) =>
        localizeHelpCategory(category, isFrench),
      ),
    [audience, isFrench]
  );
  const articles = React.useMemo(
    () =>
      getHelpArticlesForAudience(audience).map((article) =>
        localizeHelpArticle(article, isFrench),
      ),
    [audience, isFrench]
  );
  const matchedArticles = React.useMemo(
    () =>
      getHelpArticlesForAudience(audience).map(article => localizeHelpArticle(article, isFrench)).filter(article => [article.title, article.summary, article.categoryTitle, ...(article.steps || [])].join(' ').toLocaleLowerCase().includes(query.trim().toLocaleLowerCase())),
    [audience, isFrench, query]
  );
  const roleCopy = getHelpRoleCopy(audience);
  const localizedRoleCopy = isFrench
    ? {
        roleLabel:
          audience === "doctor" ? "médecin" : audience === "pharmacy" ? "pharmacie" : "patient",
        intro:
          audience === "doctor"
            ? "Guide pas à pas pour les médecins : comptes, profils cliniques, rendez-vous, messages, prescriptions, confidentialité et assistance. Prévu pour devenir une bibliothèque de parcours illustrés à mesure que le produit mûrit."
            : audience === "pharmacy"
              ? "Guide pas à pas pour les pharmacies : onboarding, configuration de l’officine, stock, exécution des prescriptions, paiements, Fola™, permissions d’équipe, notifications et assistance."
              : "Guide pas à pas pour les patients : comptes, profils, rendez-vous, messages, Fola, prescriptions, confidentialité et assistance. Prévu pour devenir une bibliothèque de parcours illustrés à mesure que le produit mûrit.",
        section:
          audience === "doctor"
            ? "Commencez par l’espace médecin que vous essayez d’utiliser. Les sections en développement sont prêtes pour des captures et futurs articles."
            : audience === "pharmacy"
              ? "Commencez par l’espace pharmacie concerné. Chaque guide distingue les instructions de l’officine des démonstrations en Staging."
              : "Commencez par l’espace patient que vous essayez d’utiliser. Les sections en développement sont prêtes pour des captures et futurs articles.",
        foundation:
          audience === "doctor"
            ? "La base médecin met l’accent sur la confiance du profil, le flux de rendez-vous et l’accompagnement clinique sûr par rôle."
            : audience === "pharmacy"
              ? "La base pharmacie met l’accent sur la confiance du tableau de bord, les flux d’exécution de démonstration, la visibilité du stock, les paiements et les opérations d’officine sûres par rôle."
              : "La base patient met l’accent sur la création du compte, la confiance du profil et l’accompagnement sûr de Fola.",
      }
    : roleCopy;
  const focusHelpSearch = React.useCallback(() => {
    window.setTimeout(() => {
      const input = document.querySelector(
        `input[placeholder="${isFrench ? "Rechercher dans l’aide AfriMed™" : "Search AfriMed™ guidance"}"]`,
      );
      input?.focus?.();
    }, 0);
  }, [isFrench]);

  return (
    <HelpRailShell pharmacyShell={pharmacyShell}>
      <TopBarCenter
        title={isFrench ? "Centre d’aide" : "Help Centre"}
        onBack={() => navigate(-1)}
        titleClassName="text-title-large text-on-surface"
      />

      <div className="w-full min-w-0 space-y-6">
        {audience === "pharmacy" && <PharmacyGuideNotice isFrench={isFrench} />}
        <section className="space-y-4">
          <div>
            <div className="text-label-large text-[rgb(var(--afrimed-primary))]">
              {isFrench ? "Aide et accompagnement" : "Help & guidance"}
            </div>
            <h1 className="mt-2 text-title-large text-on-surface">
              {isFrench
                ? "Sachez exactement quoi faire ensuite dans AfriMed™."
                : "Know exactly what to do next in AfriMed™."}
            </h1>
            <p className="mt-3 max-w-[760px] text-body-large text-on-surface-var">
              {localizedRoleCopy.intro}
            </p>
          </div>
          {!hasAudienceOverride ? (
            <HelpAudienceSwitch audience={audience} onChange={setAudience} isFrench={isFrench} />
          ) : null}
        </section>

        {pharmacyShell ? (
          <PharmacyFolaSmartCard
            title={isFrench ? "Trouver l’aide pharmacie" : "Find pharmacy guidance"}
            body={
              isFrench
                ? "Utilisez cette page pour rechercher l’aide pharmacie sur l’onboarding, la vérification du profil, le stock, l’exécution des prescriptions, les paiements, les messages, les alertes, les rôles d’équipe et l’assistance technique."
                : "Use this page to search pharmacy guidance on onboarding, profile verification, inventory, prescription fulfilment, payments, messages, alerts, team roles, and technical support."
            }
            actions={[
              {
                label: isFrench ? "Messages et Fola" : "Messages & Fola",
                route: "/pharmacy/messages/fola",
                promptKey: "pharmacy_help",
              },
              {
                label: isFrench ? "Assistance technique" : "Technical support",
                route: "/pharmacy/help/technical-support",
              },
            ]}
            returnTo="/pharmacy/help"
          />
        ) : null}

        <HelpSearchBar
          value={query}
          onChange={setQuery}
          placeholder={isFrench ? "Rechercher dans l’aide AfriMed™" : "Search AfriMed™ guidance"}
        />

        {query.trim() ? (
          <HelpSection
            title={isFrench ? "Résultats de recherche" : "Search results"}
            description={
              isFrench
                ? "Guides correspondant à votre recherche."
                : "Guides matching your current search."
            }
          >
            {matchedArticles.length ? (
              <div className="grid gap-3 md:grid-cols-2">
                {matchedArticles.map((article) => (
                  <HelpArticleCard
                    key={`${article.categorySlug}-${article.slug}`}
                    article={article}
                    basePath={basePath}
                  />
                ))}
              </div>
            ) : (
              <HelpEmptyState
                title={isFrench ? "Aucun guide ne correspond à cette recherche" : "No guide matched that search"}
                body={
                  isFrench
                    ? "Essayez un mot plus simple comme profil, Fola, assurance, mot de passe ou messages."
                    : "Try a simpler word such as profile, Fola, insurance, password, or messages."
                }
              />
            )}
          </HelpSection>
        ) : null}

        <HelpSection
          title={isFrench ? "Sections d’aide" : "Guidance sections"}
          description={localizedRoleCopy.section}
        >
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {categories.map((category) => (
              <HelpCategoryCard
                key={category.slug}
                category={category}
                basePath={basePath}
                isFrench={isFrench}
              />
            ))}
          </div>
        </HelpSection>

        <HelpSection
          title={isFrench ? "Premiers parcours guidés" : "First guided experiences"}
          description={localizedRoleCopy.foundation}
        >
          <div className="grid gap-3 md:grid-cols-2">
            {articles.slice(0, 8).map((article) => (
              <HelpArticleCard
                key={`${article.categorySlug}-${article.slug}`}
                article={article}
                basePath={basePath}
              />
            ))}
          </div>
        </HelpSection>
      </div>
    </HelpRailShell>
  );
}

function PharmacyHelpHeader() {
  const [notifications, setNotifications] = React.useState(() => readPharmacyNotifications());

  React.useEffect(() => {
    const refreshNotifications = () => setNotifications(readPharmacyNotifications());
    refreshNotifications();
    window.addEventListener("storage", refreshNotifications);
    window.addEventListener(PHARMACY_NOTIFICATIONS_CHANGED_EVENT, refreshNotifications);
    return () => {
      window.removeEventListener("storage", refreshNotifications);
      window.removeEventListener(PHARMACY_NOTIFICATIONS_CHANGED_EVENT, refreshNotifications);
    };
  }, []);

  return <PharmacyDesktopHeader section="help" notifications={notifications} />;
}

function HelpRailShell({ children, pharmacyShell = false }) {
  if (!pharmacyShell) {
    return (
      <div
        className="w-full min-w-0 overflow-x-hidden"
        data-help-canonical-body-rail
      >
        {children}
      </div>
    );
  }

  return (
    <>
      <PharmacyHelpHeader />
      <LayoutShell
        hideHeader
        freeScroll
        shellClassName="overflow-x-hidden"
        railClassName="afrimed-rail afrimed-rail--comfortable"
      >
        {children}
      </LayoutShell>
    </>
  );
}

function HelpCategoryPage({ audienceOverride = "", basePath = "/help", pharmacyShell = false }) {
  const navigate = useNavigate();
  const { languageCode } = useHelpLanguage();
  const isFrench = isFrenchLanguage(languageCode);
  const { categorySlug } = useParams();
  const [audience, setAudience] = useHelpAudience(audienceOverride);
  const hasAudienceOverride = Boolean(String(audienceOverride || "").trim());
  const category = localizeHelpCategory(
    resolveHelpCategory(categorySlug, audience),
    isFrench,
  );
  const categories = React.useMemo(
    () =>
      getHelpCategoriesForAudience(category?.audience || audience).map((item) =>
        localizeHelpCategory(item, isFrench),
      ),
    [audience, category?.audience, isFrench]
  );

  if (!category) return <Navigate to={helpPath(basePath, '', '', audience, languageCode)} replace />;

  const hasArticles = Boolean(category.articles?.length);

  return (
    <HelpRailShell pharmacyShell={pharmacyShell}>
      <TopBarCenter
        title={category.title}
        onBack={() => navigate(-1)}
        titleClassName="text-title-large text-on-surface"
      />

      <div className="grid w-full min-w-0 gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
        <HelpSidebar
          categories={categories}
          activeSlug={category.slug}
          basePath={basePath}
          isFrench={isFrench}
        />
        <div className="min-w-0 space-y-7">
          {category.audience === "pharmacy" && <PharmacyGuideNotice isFrench={isFrench} />}
          <HelpMobileSectionNav
            categories={categories}
            activeSlug={category.slug}
            basePath={basePath}
            isFrench={isFrench}
          />
          {!hasAudienceOverride ? (
              <HelpAudienceSwitch
                audience={category.audience || audience}
                onChange={(next) => {
                  setAudience(next);
                  navigate(helpPath(basePath, '', '', next, languageCode));
                }}
                isFrench={isFrench}
              />
          ) : null}

          <section className="min-w-0 px-1 pt-1 md:pt-2">
            <div className="text-label-large text-[rgb(var(--afrimed-primary))]">
              {category.status}
            </div>
            <p className="mt-2 max-w-[760px] break-words text-body-large text-on-surface-var">
              {category.summary}
            </p>
          </section>

          {hasArticles ? (
            <HelpSection
              title="Guides"
              description={
                isFrench
                  ? "Chaque guide prend en charge des étapes ordonnées, des captures d’écran et des encadrés de confiance."
                  : "Each guide supports ordered steps, screenshots, and trust-safe callouts."
              }
            >
              <div className="grid min-w-0 gap-3 md:grid-cols-2">
                {category.articles.map((article) => (
                  <HelpArticleCard
                    key={article.slug}
                    basePath={basePath}
                    article={{
                      ...article,
                      categorySlug: category.slug,
                      categoryTitle: category.title,
                      audience: category.audience,
                    }}
                  />
                ))}
              </div>
            </HelpSection>
          ) : (
            <HelpEmptyState
              title={isFrench ? "En développement" : "Currently expanding"}
              body={
                isFrench
                  ? "Cette section fait partie de l’architecture du centre d’aide et est prête pour des articles guidés par captures lors du prochain passage de contenu."
                  : "This section is part of the Help Centre architecture and is ready for screenshot-led articles in the next content pass."
              }
              action={
                <Link to={"/help-support"}>
                  <Button variant="tonal" iconName="MessageSquarePlus" iconPosition="left">
                    {isFrench ? "Contacter l’assistance" : "Contact support"}
                  </Button>
                </Link>
              }
            />
          )}
        </div>
      </div>
    </HelpRailShell>
  );
}

function HelpArticlePage({ audienceOverride = "", basePath = "/help", pharmacyShell = false }) {
  const navigate = useNavigate();
  const { languageCode } = useHelpLanguage();
  const isFrench = isFrenchLanguage(languageCode);
  const { categorySlug, articleSlug } = useParams();
  const [audience, setAudience] = useHelpAudience(audienceOverride);
  const hasAudienceOverride = Boolean(String(audienceOverride || "").trim());
  const article = localizeHelpArticle(
    resolveHelpArticle(categorySlug, articleSlug, audience),
    isFrench,
  );
  const categories = React.useMemo(
    () =>
      getHelpCategoriesForAudience(article?.audience || audience).map((item) =>
        localizeHelpCategory(item, isFrench),
      ),
    [audience, article?.audience, isFrench]
  );

  if (!article) return <Navigate to={helpPath(basePath, categorySlug || '', '', audience, languageCode)} replace />;

  const screenshots = article.screenshots || (article.screenshot ? [article.screenshot] : []);

  return (
    <HelpRailShell pharmacyShell={pharmacyShell}>
      <TopBarCenter
        title={
          isFrench
            ? pharmacyShell
              ? "Centre d’aide pharmacie"
              : "Guide d’aide"
            : pharmacyShell
              ? "Pharmacy Help Centre"
              : "Help guide"
        }
        onBack={() => navigate(-1)}
        titleClassName="text-title-large text-on-surface"
      />

      <div className="grid w-full min-w-0 gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
        <HelpSidebar
          categories={categories}
          activeSlug={article.categorySlug}
          basePath={basePath}
          isFrench={isFrench}
        />

        <article className="min-w-0 space-y-6">
          {article.audience === "pharmacy" && <PharmacyGuideNotice isFrench={isFrench} />}
          {!hasAudienceOverride ? (
              <HelpAudienceSwitch
                audience={article.audience || audience}
                onChange={(next) => {
                  setAudience(next);
                  navigate(helpPath(basePath, '', '', next, languageCode));
                }}
                isFrench={isFrench}
              />
          ) : null}

          <div className="flex flex-wrap items-center gap-2 text-label-large">
            <Link to={helpPath(basePath, '', '', article.audience, languageCode)} className="text-on-surface-var hover:text-[rgb(var(--afrimed-primary))]">
              {isFrench ? "Centre d’aide" : "Help Centre"}
            </Link>
            <span className="text-on-surface-var">/</span>
            <Link
              to={helpPath(basePath, article.categorySlug, '', article.audience, languageCode)}
              className="text-on-surface-var hover:text-[rgb(var(--afrimed-primary))]"
            >
              {article.categoryTitle}
            </Link>
          </div>

          <SettingsCard fullWidth className="min-w-0 px-5 py-6 md:px-7">
            <div className="text-label-large text-[rgb(var(--afrimed-primary))]">
              {article.categoryTitle}
            </div>
            <h1 className="mt-2 break-words text-title-large text-on-surface">{article.title}</h1>
            <p className="mt-3 max-w-[760px] break-words text-body-large text-on-surface-var">
              {article.summary}
            </p>
          </SettingsCard>

          {screenshots.length ? (
            <div className="w-full min-w-0 space-y-5">
              {screenshots.map((screenshot, index) => (
                <HelpScreenshotFrame
                  key={`${screenshot.src || screenshot.label || "screenshot"}-${index}`}
                  screenshot={screenshot}
                />
              ))}
            </div>
          ) : null}

          <SettingsCard fullWidth className="min-w-0 px-5 py-6 md:px-7">
            <h2 className="text-title-large text-on-surface">{isFrench ? "Étapes" : "Steps"}</h2>
            <div className="mt-4">
              <HelpStepList steps={article.steps} />
              {article.publicSupportLink && <p className="mt-4 text-body-medium"><a className="underline" href={supportContact.contactUrls[isFrench ? "fr" : "en"]}>{isFrench ? "Assistance" : "Support"}</a></p>}
            </div>
          </SettingsCard>

          {article.demoSteps?.length ? <SettingsCard fullWidth className="min-w-0 px-5 py-6 md:px-7">
            <h2 className="text-title-large text-on-surface">{isFrench ? 'Démonstration en Staging uniquement' : 'Staging demonstration only'}</h2>
            <div className="mt-4"><HelpStepList steps={article.demoSteps} /></div>
          </SettingsCard> : null}

          {article.questions?.length ? <SettingsCard fullWidth className="min-w-0 px-5 py-6 md:px-7">
            <h2 className="text-title-large text-on-surface">{isFrench ? "Questions fréquentes" : "Frequently asked questions"}</h2>
            <div className="mt-4 space-y-6">{article.questions.map(([question,answer])=><section key={question}>
              <h3 className="text-title-medium text-on-surface">{question}</h3>
              <p className="mt-2 text-body-medium text-on-surface-var">{answer}</p>
            </section>)}</div>
          </SettingsCard> : null}
          {article.slug === 'clinician-payout-account' ? <Link to="/legal/terms" className="inline-block text-body-medium text-primary">{isFrench ? 'Conditions générales' : 'Terms & Conditions'}</Link> : null}
          <HelpCallout callout={article.callout} />

          <HelpSupportActionBlock
            onBack={() => navigate(helpPath(basePath, article.categorySlug, '', article.audience, languageCode))}
            supportTo={`/help-support?lang=${isFrench ? "fr" : "en-GB"}${article.audience === "pharmacy" ? "&topic=technical_issue" : ""}`}
            isFrench={isFrench}
          />
        </article>
      </div>
    </HelpRailShell>
  );
}

export default function HelpCentre({
  audienceOverride = "",
  basePath = "/help",
  pharmacyShell = false,
} = {}) {
  useSupportLanguage();
  const { categorySlug, articleSlug } = useParams();

  if (categorySlug && articleSlug) {
    return (
      <HelpArticlePage
        audienceOverride={audienceOverride}
        basePath={basePath}
        pharmacyShell={pharmacyShell}
      />
    );
  }
  if (categorySlug) {
    return (
      <HelpCategoryPage
        audienceOverride={audienceOverride}
        basePath={basePath}
        pharmacyShell={pharmacyShell}
      />
    );
  }
  return (
    <HelpLanding
      audienceOverride={audienceOverride}
      basePath={basePath}
      pharmacyShell={pharmacyShell}
    />
  );
}
